<template>
  <div class="stock-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4" v-if="!isDataLoading">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-warehouse me-2 text-primary"></i>
            <strong>Stock Management</strong>
          </h4>
          <small class="text-muted">Manage vehicle stock information, purchase details, registration, and sales</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading Overlay -->
    <div v-if="isDataLoading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" size="lg" class="mb-3" />
        <h5 class="text-muted">Loading stock data...</h5>
      </div>
    </div>

    <div v-if="!isDataLoading">
      <CRow>
        <CCol sm="12" md="12" lg="12" xl="12" xxl="9">
          <CCard>
            <CCardHeader>
              <strong>Stock</strong> {{ stokHeader }}
            </CCardHeader>
            <CCardBody>
              <CTabs add-tab-classes="mt-1" :active-tab.sync="activeTab">
                <CTab v-if="canViewStockInfo">
                  <template slot="title">
                    <i class="fas fa-info-circle"></i> Stock Info
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CRow
                          form
                          class="form-group"
                          v-if="!isNewApplication()"
                        >
                          <CCol tag="label" sm="3" class="col-form-label">
                            Record Date
                          </CCol>
                          <CCol sm="9">
                            <input
                              readonly
                              type="datetime-local"
                              :value="computeSystemRegistrationDate"
                              @change="setSystemRegistrationDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>
                        <CInput
                          label="Stock No"
                          horizontal
                          :readonly="!isNewApplication()"
                          v-model="$v.obj.stockNo.$model"
                          :isValid="checkIfValid('stockNo')"
                          invalidFeedback="This is a required field and must be at least 2 characters"
                        >
                          <template #append>
                            <CButton color="primary" @click="onChangeStockNo()">
                              Change
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          label="Showroom"
                          horizontal
                          readonly
                          v-model="selectedShowroom.lotNo"
                          v-if="!isNewApplication()"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchShowroom()"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>

                        <CSelect
                          horizontal
                          v-if="!isNewApplication()"
                          :value.sync="selectedArrivalState"
                          :options="arrivalStates"
                          label="Arrival"
                          :disabled="isReadOnly"
                        />
                        <CRow
                          form
                          class="form-group"
                          v-if="!isNewApplication()"
                        >
                          <CCol tag="label" sm="3" class="col-form-label">
                            State
                          </CCol>
                          <CCol sm="9">
                            <CInputCheckbox
                              label="Available"
                              :checked.sync="obj.isOpen"
                            />
                            <CInputCheckbox
                              label="Booking"
                              :checked="obj.isBooked"
                              @update:checked="onBookingToggle($event)"
                            />
                            <CInputCheckbox
                              label="LOU"
                              :checked="obj.isLou"
                              @update:checked="onLouToggle($event)"
                            />
                            <CInputCheckbox
                              label="Sold"
                              :checked.sync="obj.isSold"
                            />
                            <CInputCheckbox
                              label="Request Duty"
                              :checked.sync="obj.isRequestDuty"
                            />
                          </CCol>
                        </CRow>
                        <CRow
                          form
                          class="form-group"
                          v-if="!isNewApplication() && obj.isBooked"
                        >
                          <CCol tag="label" sm="3" class="col-form-label">
                            Booking Details
                          </CCol>
                          <CCol sm="9">
                            <CRow form class="form-group">
                              <CCol tag="label" sm="3" class="col-form-label">
                                Booking From
                              </CCol>
                              <CCol sm="9">
                                <input
                                  type="date"
                                  :value="computeBookingStartDate"
                                  @change="setBookingStartDate"
                                  class="mr-2"
                                />
                              </CCol>
                            </CRow>

                            <CRow form class="form-group">
                              <CCol tag="label" sm="3" class="col-form-label">
                                Booking Expiry
                              </CCol>
                              <CCol sm="9">
                                <input
                                  type="date"
                                  :value="computeBookingEndDate"
                                  @change="setBookingEndDate"
                                  class="mr-2"
                                />
                              </CCol>
                            </CRow>
                          </CCol>
                        </CRow>
                        <CRow
                          form
                          class="form-group"
                          v-if="!isNewApplication() && obj.isLou"
                        >
                          <CCol tag="label" sm="3" class="col-form-label">
                            LOU Details
                          </CCol>
                          <CCol sm="9">
                            <CRow form class="form-group">
                              <CCol tag="label" sm="3" class="col-form-label">
                                LOU From
                              </CCol>
                              <CCol sm="9">
                                <input
                                  type="date"
                                  :value="computeLouStartDate"
                                  @change="setLouStartDate"
                                  class="mr-2"
                                />
                              </CCol>
                            </CRow>

                            <CRow form class="form-group">
                              <CCol tag="label" sm="3" class="col-form-label">
                                LOU Expiry
                              </CCol>
                              <CCol sm="9">
                                <input
                                  type="date"
                                  :value="computeLouEndDate"
                                  @change="setLouEndDate"
                                  class="mr-2"
                                />
                              </CCol>
                            </CRow>
                          </CCol>
                        </CRow>
                        <!-- <CSelect
                          label="Current Status"
                          horizontal
                          :options="stockStatuses"
                          :value.sync="selectedStockStatusId"
                          v-if="!isNewApplication()"
                          ><template #append>
                            <CButton
                              color="primary"
                              @click="updateStockStatus()"
                            >
                              Update
                            </CButton>
                          </template>
                        </CSelect>

                        <CRow
                          form
                          class="form-group"
                          v-if="!isNewApplication()"
                        >
                          <CCol tag="label" sm="3" class="col-form-label">
                            Status History
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="obj.stockStatusHistories"
                              :fields="stockStatusHistoryFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #status_name="{ item }">
                                <td>
                                  {{ item.stockStatus.name }}
                                </td>
                              </template>

                              <template #date_time="{ item }">
                                <td>
                                  {{ getDisplayDateTime(item.dateTime) }}
                                </td>
                              </template>
                              <template #profile_name="{ item }">
                                <td>
                                  {{ getProfileFullName(item) }}
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow> -->
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>
                <CTab v-if="!isNewApplication() && canViewVehicleInfo">
                  <template slot="title">
                    <i class="fas fa-car"></i> Vehicle Info
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Brand"
                          horizontal
                          readonly
                          v-model="selectedBrand.name"
                        >
                          <template #append>
                            <CButton color="primary" @click="onSearchBrand()">
                              Search
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          label="Model"
                          horizontal
                          readonly
                          v-model="selectedModel.name"
                          :description="selectedBrand.id ? '' : 'Pick a Brand first to enable Model search'"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchModel()"
                              :disabled="!selectedBrand.id"
                              :title="!selectedBrand.id ? 'Pick a Brand first' : ''"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          label="Chasis No"
                          horizontal
                          v-model="obj.vehicle.chasisNo"
                          :isValid="checkIfValid('vehicle.chasisNo')"
                          invalidFeedback="Chassis number must be at least 3 characters"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Code Model"
                          horizontal
                          v-model="obj.vehicle.codeModel"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Engine No"
                          horizontal
                          v-model="obj.vehicle.engineNo"
                          :isValid="checkIfValid('vehicle.engineNo')"
                          invalidFeedback="Engine number must be at least 3 characters"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Engine Capacity"
                          horizontal
                          v-model="obj.vehicle.engineCapacity"
                          :readonly="isReadOnly"
                        />
                        <CSelect
                          label="Vehicle Type"
                          horizontal
                          :options="vehicleTypes"
                          :value.sync="selectedVehicleTypeId"
                          @change="onVehicleTypeSelect()"
                          :disabled="isReadOnly"
                        >
                        </CSelect>

                        <CSelect
                          label="Month"
                          horizontal
                          :options="[
                            '',
                            '01',
                            '02',
                            '03',
                            '04',
                            '05',
                            '06',
                            '07',
                            '08',
                            '09',
                            '10',
                            '11',
                            '12',
                          ]"
                          :value.sync="obj.vehicle.month"
                          :disabled="isReadOnly"
                        />
                        <CSelect
                          label="Year"
                          horizontal
                          :options="vehicleYear"
                          :value.sync="obj.vehicle.year"
                          :disabled="isReadOnly"
                        />

                        <CInput
                          label="Color"
                          horizontal
                          v-model="obj.vehicle.color"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="External Link"
                          horizontal
                          v-model="obj.vehicle.externalLink"
                          :readonly="isReadOnly"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="openLink(obj.vehicle.externalLink)"
                              >Open</CButton
                            >
                          </template>
                        </CInput>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Is Supplier Image Available?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="
                                obj.vehicle.isSupplierImageAvailable
                              "
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Is Local Image Available?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="obj.vehicle.isLocalImageAvailable"
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Description
                          </CCol>
                          <CCol sm="9">
                            <CTextarea
                              placeholder=""
                              rows="5"
                              v-model="obj.vehicle.description"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Images
                          </CCol>
                          <CCol sm="9">
                            <CRow>
                              <CCol
                                v-for="(item, index) in obj.vehicle
                                  .vehiclePhotoList"
                                :key="item.id"
                                xs="12"
                                sm="6"
                                md="4"
                                lg="3"
                                xl="2"
                                class="d-flex justify-content-center position-relative mb-4"
                              >
                                <div class="image-container">
                                  <CImg
                                    :src="getImageUrl(item)"
                                    thumbnail
                                    class="img-fluid"
                                    style="max-width: 100%; cursor: pointer"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                  <!-- Buttons overlay -->
                                  <div class="button-overlay">
                                    <div class="button-row">
                                      <CButton
                                        size="sm"
                                        color="primary"
                                        class="move-button"
                                        @click="moveImageUp(index)"
                                        :disabled="index === 0"
                                      >
                                        ▲
                                      </CButton>
                                      <CButton
                                        size="sm"
                                        color="primary"
                                        class="move-button"
                                        @click="moveImageDown(index)"
                                        :disabled="
                                          index ===
                                          sortedVehiclePhotos.length - 1
                                        "
                                      >
                                        ▼
                                      </CButton>
                                    </div>
                                    <div class="button-row">
                                      <CButton
                                        size="sm"
                                        color="danger"
                                        class="move-button"
                                        @click="deleteImage(item.id)"
                                      >
                                        🗑
                                      </CButton>
                                      <CButton
                                        size="sm"
                                        color="success"
                                        class="move-button"
                                        @click="setDefaultImage(item)"
                                        :disabled="isDefaultImage(item)"
                                      >
                                        Default
                                      </CButton>
                                    </div>
                                    <CButton
                                      size="sm"
                                      color="info"
                                      class="move-button"
                                      @click="maximizeImage(item)"
                                    >
                                      🔍
                                    </CButton>
                                  </div>

                                  <!-- <div class="image-overlay">
                                    <CButton
                                      color="danger"
                                      size="sm"
                                      @click="deleteImage(item.id)"
                                      class="action-button"
                                    >
                                      <CIcon name="cil-trash" />
                                    </CButton>
                                    <CButton
                                      color="success"
                                      size="sm"
                                      @click="setDefaultImage(item)"
                                      class="action-button"
                                      :disabled="isDefaultImage(item)"
                                    >
                                      <CIcon name="cil-star" />
                                    </CButton>
                                    <CButton
                                      color="info"
                                      size="sm"
                                      @click="moveImageUp(index)"
                                      class="action-button"
                                      :disabled="index === 0"
                                    >
                                      <CIcon name="cil-arrow-top" />
                                    </CButton>
                                    <CButton
                                      color="info"
                                      size="sm"
                                      @click="moveImageDown(index)"
                                      class="action-button"
                                      :disabled="
                                        index === sortedVehiclePhotos.length - 1
                                      "
                                    >
                                      <CIcon name="cil-arrow-bottom" />
                                    </CButton>
                                  </div> -->

                                  <span
                                    v-if="isDefaultImage(item)"
                                    class="default-badge"
                                  >
                                    Default
                                  </span>
                                </div>
                              </CCol>
                            </CRow>
                          </CCol>
                        </CRow>
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadImage @uploaded="media_uploaded" />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>
                <CTab v-if="!isNewApplication() && canViewPurchase">
                  <template slot="title">
                    <i class="fas fa-shopping-cart"></i> Purchase
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Supplier"
                          horizontal
                          readonly
                          v-model="selectedSupplier.name"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchSupplier()"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          prepend="RM"
                          label="Body Price"
                          horizontal
                          v-model.number="obj.purchase.bodyPriceLocalCurrency"
                          type="number"
                          step="0.01"
                          :isValid="checkIfValid('purchase.bodyPriceLocalCurrency')"
                          invalidFeedback="Body price must be a valid decimal number"
                          :readonly="isReadOnly"
                        />

                        <!--
                          Purchase Payments bucket — single-bucket multi-
                          currency widget. Each payment carries its own
                          SupplierCurrency / ForeignAmount / ExchangeRate /
                          LocalAmount. The modal auto-recalculates Local =
                          Foreign × Rate but the user can override.
                        -->
                        <WidgetsPurchasePaymentBucket
                          title="Purchase Payments"
                          :payments="purchasePayments"
                          :defaultCurrency="obj.purchase.supplierCurrency || 'MYR'"
                          :isReadOnly="isReadOnly"
                          @add="onAddPurchasePayment"
                          @update="onUpdatePurchasePayment"
                          @remove="onRemovePurchasePayment"
                        />
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewImport">
                  <template slot="title">
                    <i class="fas fa-ship"></i> Import
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Ship Name"
                          horizontal
                          v-model="obj.import.shipName"
                          :readonly="isReadOnly"
                        />
                        <!-- <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Estimated Departure Date
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="datetime-local"
                              :value="computeEstimateDateOfDeparture"
                              @change="setEstimateDateOfDeparture"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow> -->
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            BOL/BL Date
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeDateOfBillOfLading"
                              @change="setDateOfBillOfLading"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Estimated Arrival Date (ETA)
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeEstimateDateOfArrival"
                              @change="setEstimateDateOfArrival"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>
                        <!-- <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Bill Of Landing (BOL) Documents
                          </CCol>
                          <CCol sm="9"> 
                            <CImg
                              v-for="item in obj.vehicle.vehiclePhotoList"
                              :key="item.id"
                              :src="getImageUrl(item)"
                              class="mb-2"
                              thumbnail
                              @click="onImageClick(item)"
                            ></CImg>
                          </CCol>
                        </CRow> -->

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            BOL/BL Documents
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedBolDocumentList"
                              :fields="bolDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeBolDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="bol_uploaded" />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewClearance">
                  <template slot="title">
                    <i class="fas fa-file-import"></i> Clearance
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Aprroved Permit No"
                          horizontal
                          v-model="obj.clearance.approvedPermitNo"
                          :readonly="isReadOnly"
                        />
                        <CInput
    label="Lot No"
    horizontal
    v-model="obj.clearance.lotNo"
    :readonly="isReadOnly"
  />
                        <CInput
                          label="K8 Document No"
                          horizontal
                          v-model="obj.clearance.k8DocumentNo"
                          :readonly="isReadOnly"
                        />
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            K8 Documents
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedK8DocumentList"
                              :fields="k8DocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeK8DocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="k8_uploaded" />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            SIJIL EXPORT (Export Certificate)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedExportCertificateDocumentList"
                              :fields="exportCertificateDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeExportCertificateDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="exportCertificate_uploaded" />
                          </CCol>
                        </CRow>

                        <CInput
                          label="K1 Document No"
                          horizontal
                          v-model="obj.clearance.k1DocumentNo"
                          :readonly="isReadOnly"
                        />

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            K1 Documents
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedK1DocumentList"
                              :fields="k1DocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeK1DocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="k1_uploaded" />
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewSale">
                  <template slot="title">
                    <i class="fas fa-handshake"></i> Sale
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Salesman"
                          horizontal
                          readonly
                          v-model="selectedSalesman.fullName"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchSalesMan()"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          label="Customer"
                          horizontal
                          readonly
                          v-model="selectedCustomer.name"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchCustomer()"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            IC CUSTOMER (Customer IC)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedCustomerIcDocumentList"
                              :fields="customerIcDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeCustomerIcDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="customerIc_uploaded" />
                          </CCol>
                        </CRow>
                        <CInput
                          label="Selling Price"
                          prepend="RM"
                          horizontal
                          v-model="computedSellingPrice"
                          :disabled="true"
                          description="Auto calculated from LOU Purchase Price - Insurance - Registration Fee - Roadtax"
                        />

                        <CInput
                          label="LOU Purchase Price"
                          prepend="RM"
                          horizontal
                          v-model.number="obj.sale.louPurchasePrice"
                          type="number"
                          step="0.01"
                          placeholder="Enter LOU Purchase Price"
                          :isValid="checkIfValid('sale.louPurchasePrice')"
                          invalidFeedback="LOU Purchase Price must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          prepend="RM"
                          label="Insurance"
                          v-model.number="obj.adminitrativeCost.insuranceAmount"
                          type="number"
                          step="0.01"
                          horizontal
                          :isValid="checkIfValid('adminitrativeCost.insuranceAmount')"
                          invalidFeedback="Insurance amount must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Insurance Company"
                          v-model="obj.adminitrativeCost.insuranceCompanyName"
                          horizontal
                          placeholder="e.g. Zurich Takaful"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          prepend="RM"
                          label="Registration Fee"
                          v-model.number="obj.adminitrativeCost.registrationFeeAmount"
                          type="number"
                          step="0.01"
                          horizontal
                          :isValid="checkIfValid('adminitrativeCost.registrationFeeAmount')"
                          invalidFeedback="Registration fee must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          prepend="RM"
                          label="Roadtax"
                          v-model.number="obj.adminitrativeCost.roadtaxAmount"
                          type="number"
                          step="0.01"
                          horizontal
                          :isValid="checkIfValid('adminitrativeCost.roadtaxAmount')"
                          invalidFeedback="Roadtax amount must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          prepend="RM"
                          label="Body Price"
                          v-model.number="obj.adminitrativeCost.bodyPrice"
                          horizontal
                          type="number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Processing Fee / Warranty"
                          v-model="obj.sale.warranty"
                          horizontal
                          placeholder="Enter processing fee / warranty"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Warranty Type"
                          v-model="obj.sale.warrantyType"
                          horizontal
                          placeholder="e.g. 7 + 1 Years"
                          :readonly="isReadOnly"
                        />

                        <CInput
                          label="Deposit Amount"
                          prepend="RM"
                          horizontal
                          v-model="computedDepositAmount"
                          :disabled="true"
                          description="Auto calculated from LOU Purchase Price - Approved Loan Amount"
                        />
                        <CInput
                          label="Trade In Amount"
                          prepend="RM"
                          horizontal
                          description="Use for Disbursement Invoice Full"
                          v-model="obj.sale.tradeInAmount"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Handling Fee"
                          prepend="RM"
                          horizontal
                          description="Use for Disbursement Invoice EOE"
                          v-model="obj.sale.eoeAmount"
                          :readonly="isReadOnly"
                        />
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Is Bank Loan?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="obj.sale.isUseLoan"
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>
                        <!-- <CInput
                          label="Bank Name"
                          horizontal
                          v-model="obj.sale.loan.bankName"
                        /> -->
                        <CInput
                          label="Bank"
                          horizontal
                          readonly
                          v-model="selectedBank.name"
                          v-if="obj.sale.isUseLoan"
                        >
                          <template #append>
                            <CButton color="primary" @click="onSearchBank()">
                              Search
                            </CButton>
                          </template>
                        </CInput>

                        <!-- <CInput
                          label="Bank Deposit"
                          prepend="RM"
                          horizontal
                          v-model="obj.sale.loan.depositAmount"
                          v-if="obj.sale.isUseLoan"
                        /> -->
                        <!-- <CInput
                          label="Requested Loan"
                          prepend="RM"
                          horizontal
                          v-model="obj.sale.loan.requestedLoanAmount"
                          v-if="obj.sale.isUseLoan"
                        /> -->
                        <CInput
                          label="Loan Tenure"
                          horizontal
                          v-model="obj.sale.loan.loanTenure"
                          v-if="obj.sale.isUseLoan"
                          :readonly="isReadOnly"
                        />
                        <!-- <CInput
                          label="Bank Unpaid Balance"
                          prepend="RM"
                          horizontal
                          v-model="obj.sale.loan.unpaidBalanceAmount"
                          v-if="obj.sale.isUseLoan"
                        /> -->
                        <CInput
                          label="Approved Loan Amount"
                          prepend="RM"
                          horizontal
                          v-model.number="obj.sale.loan.approvedLoanAmount"
                          type="number"
                          step="0.01"
                          v-if="obj.sale.isUseLoan"
                          :isValid="checkIfValid('sale.loan.approvedLoanAmount')"
                          invalidFeedback="Approved loan amount is required when using loan and must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="HP Serial"
                          horizontal
                          v-model="obj.sale.loan.hpSerial"
                          v-if="obj.sale.isUseLoan"
                          placeholder="Hire Purchase Serial No (Bank Reference)"
                          :readonly="isReadOnly"
                        />
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Letter Of Undertaking (LOU)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedLouDocumentList"
                              :fields="louDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeLouDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="lou_uploaded" />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>

                        <!--
                          Customer Payments bucket — replaces the legacy
                          bare-upload "Receipt" section. Each row = one
                          payment received from the customer (Amount + Date +
                          Reference + Method + receipt scan). Total received
                          shows in the bucket header.
                        -->
                        <WidgetsSalePaymentBucket
                          title="Customer Payments"
                          :payments="salePayments"
                          :isReadOnly="isReadOnly"
                          @add="onAddSalePayment"
                          @update="onUpdateSalePayment"
                          @remove="onRemoveSalePayment"
                        />
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewPricing">
                  <template slot="title">
                    <i class="fas fa-tag"></i> Pricing
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Recommended Sale Price"
                          prepend="RM"
                          horizontal
                          v-model.number="obj.pricing.recommendedSalePrice"
                          type="number"
                          step="0.01"
                          :isValid="checkIfValid('pricing.recommendedSalePrice')"
                          invalidFeedback="Recommended sale price must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Minimum Sale Price"
                          prepend="RM"
                          horizontal
                          v-model.number="obj.pricing.minimumSalePrice"
                          type="number"
                          step="0.01"
                          :isValid="checkIfValid('pricing.minimumSalePrice')"
                          invalidFeedback="Minimum sale price must be a valid decimal number"
                          :readonly="isReadOnly"
                        />
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>
                <CTab v-if="!isNewApplication() && canViewArrivalChecklist">
                  <template slot="title">
                    <i class="fas fa-clipboard-check"></i> Arrival Checklist
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Items
                          </CCol>
                          <CCol sm="9">
                            <CCard>
                              <CCardBody>
                                <CDataTable
                                  :items="computedArrivalChecklistList"
                                  :fields="arrivalChecklistFieldList"
                                  striped
                                  caption="Striped Table"
                                >
                                  <template #remove="{ item }">
                                    <td class="py-2">
                                      <CButton
                                        size="sm"
                                        color="info"
                                        class="ml-1"
                                        @click="onEditArrivalItem(item)"
                                      >
                                        Edit
                                      </CButton>
                                      <CButton
                                        size="sm"
                                        color="danger"
                                        class="ml-1"
                                        @click="
                                          showDeleteArrivalItemConfirmation(
                                            item
                                          )
                                        "
                                      >
                                        Delete
                                      </CButton>
                                    </td>
                                  </template>
                                </CDataTable></CCardBody
                              >
                              <CCardFooter>
                                <CButton
                                  color="primary"
                                  @click="addArrivalItemPopup()"
                                >
                                  Add
                                </CButton></CCardFooter
                              >
                            </CCard>
                          </CCol>
                        </CRow>
                        <!-- <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <CCard>
                              <CCardHeader
                                ><strong> Add New Item</strong></CCardHeader
                              >
                              <CCardBody>
                                <CInput
                                  label="Item Name"
                                  horizontal
                                  v-model="newCheckListItem.name"
                                />
                                <CRow form class="form-group">
                                  <CCol
                                    tag="label"
                                    sm="3"
                                    class="col-form-label"
                                  >
                                    Is Available?
                                  </CCol>
                                  <CCol sm="9">
                                    <CSwitch
                                      class="mr-1"
                                      color="primary"
                                      :checked.sync="
                                        newCheckListItem.isAvailable
                                      "
                                      label-on="YES"
                                      label-off="NO"
                                    >
                                    </CSwitch>
                                  </CCol>
                                </CRow>

                                <CInput
                                  label="Remarks"
                                  horizontal
                                  v-model="newCheckListItem.remarks"
                                />
                              </CCardBody>
                              <CCardFooter>
                                <CButton
                                  color="primary"
                                  @click="addNewChecklistItem()"
                                >
                                  Add
                                </CButton>
                              </CCardFooter>
                            </CCard>
                          </CCol>
                        </CRow> -->
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewRegistration">
                  <template slot="title">
                    <i class="fas fa-id-card"></i> Registration
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="RAC No"
                          horizontal
                          v-model="obj.registration.racNo"
                          :readonly="isReadOnly"
                        />
                        <CInput
                          label="Vehicle Registration No"
                          horizontal
                          v-model="obj.registration.vehicleRegistrationNumber"
                          description="Vehicle Registration Certificate a.k.a Geran Kereta"
                          :isValid="checkIfValid('registration.vehicleRegistrationNumber')"
                          invalidFeedback="Vehicle registration number must be at least 3 characters"
                          :readonly="isReadOnly"
                        />

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Vehicle Registration Date
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeVehicleRegistrationDate"
                              @change="setVehicleRegistrationDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Vehicle Registration Cert.
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedJpjGeranDocumentList"
                              :fields="jpjGeranDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="
                                      removeJpjGeranDocumentConfirmation(item)
                                    "
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument
                              @uploaded="jpjGeran_uploaded"
                            />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            INSURANS (Insurance)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedInsuranceDocumentList"
                              :fields="insuranceDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeInsuranceDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="insurance_uploaded" />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            ROADTAX (Road Tax)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedRoadTaxDocumentList"
                              :fields="roadTaxDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeRoadTaxDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="roadTax_uploaded" />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            JPJ E-Hakmilik
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedJpjHakMilikDocumentList"
                              :fields="jpjHakMilikDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="
                                      removeJpjHakMilikDocumentConfirmation(
                                        item
                                      )
                                    "
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument
                              @uploaded="jpjHakMilik_uploaded"
                            />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            RESIT KASTAM (Customs Receipt)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedReceiptKastamDocumentList"
                              :fields="receiptKastamDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeReceiptKastamDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="receiptKastam_uploaded" />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            JPJ E-Daftar
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedJpjDaftarDocumentList"
                              :fields="jpjDaftarDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="
                                      removeJpjDaftarDocumentConfirmation(item)
                                    "
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument
                              @uploaded="jpjDaftar_uploaded"
                            />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            RESIT EDAFTAR (E-Daftar Receipt)
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedReceiptEDaftarDocumentList"
                              :fields="receiptEDaftarDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="removeReceiptEDaftarDocumentConfirmation(item)"
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument @uploaded="receiptEDaftar_uploaded" />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Puspakom B2 Slip
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedPuspakomB2SlipDocumentList"
                              :fields="puspakomB2SlipDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="
                                      removePuspakomB2SlipDocumentConfirmation(
                                        item
                                      )
                                    "
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument
                              @uploaded="puspakomB2Slip_uploaded"
                            />
                            <!-- <CImg :src="uploadedImage" class="mb-2" thumbnail="true" /> -->
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Puspakom B7 Slip
                          </CCol>
                          <CCol sm="9">
                            <CDataTable
                              :items="computedPuspakomB7SlipDocumentList"
                              :fields="puspakomB7SlipDocumentFieldList"
                              striped
                              caption="Striped Table"
                            >
                              <template #document_link="{ item }">
                                <td>
                                  <CLink
                                    target="_blank"
                                    :href="item.documentUrl"
                                    >{{ item.documentName }}</CLink
                                  >
                                </td>
                              </template>
                              <template #remove="{ item }">
                                <td>
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    square
                                    size="sm"
                                    @click="
                                      removePuspakomB7SlipDocumentConfirmation(
                                        item
                                      )
                                    "
                                  >
                                    Remove
                                  </CButton>
                                </td>
                              </template>
                            </CDataTable>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <WidgetsUploadDocument
                              @uploaded="puspakomB7Slip_uploaded"
                            />
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewExpenses">
                  <template slot="title">
                    <i class="fas fa-receipt"></i> Expenses
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <!--
                          Fixed expense categories. Each bucket shows its own
                          payments table + total + Add Payment button. The
                          total is the SUM of payment amounts; the legacy
                          obj.expense.*CostAmount columns are no longer shown
                          here (still persisted in the DB for backward compat).
                        -->
                        <WidgetsExpensePaymentBucket
                          title="Svc EzCare"
                          parentType="fixed"
                          expenseType="SvcEzCare"
                          :payments="getPaymentsByType('SvcEzCare')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />
                        <WidgetsExpensePaymentBucket
                          title="Interior"
                          parentType="fixed"
                          expenseType="Interior"
                          :payments="getPaymentsByType('Interior')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />
                        <WidgetsExpensePaymentBucket
                          title="Paint"
                          parentType="fixed"
                          expenseType="Paint"
                          :payments="getPaymentsByType('Paint')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />
                        <WidgetsExpensePaymentBucket
                          title="Tyre"
                          parentType="fixed"
                          expenseType="Tyre"
                          :payments="getPaymentsByType('Tyre')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />
                        <WidgetsExpensePaymentBucket
                          title="Rental"
                          parentType="fixed"
                          expenseType="Rental"
                          :payments="getPaymentsByType('Rental')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />
                        <WidgetsExpensePaymentBucket
                          title="Others"
                          parentType="fixed"
                          expenseType="Others"
                          :payments="getPaymentsByType('Others')"
                          :isReadOnly="isReadOnly"
                          @add="onAddExpensePayment"
                          @update="onUpdateExpensePayment"
                          @remove="onRemoveExpensePayment"
                        />

                        <!-- <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                          </CCol>
                          <CCol sm="9">
                            <CCard>
                              <CCardHeader
                                ><strong> Add New Item</strong></CCardHeader
                              >
                              <CCardBody>
                                <CInput
                                  label="Item Name"
                                  horizontal
                                  v-model="newExpenseItem.name"
                                />
                                <CInput
                                  label="Amount"
                                  horizontal
                                  v-model="newExpenseItem.amount"
                                />

                                <CInput
                                  label="Remarks"
                                  horizontal
                                  v-model="newExpenseItem.remarks"
                                />
                              </CCardBody>
                              <CCardFooter>
                                <CButton
                                  color="primary"
                                  @click="addNewExpenseItem()"
                                >
                                  Add
                                </CButton>
                              </CCardFooter>
                            </CCard>
                          </CCol>
                        </CRow> -->
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewAdministrativeCost">
                  <template slot="title">
                    <i class="fas fa-file-invoice"></i> Administrative Cost
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <!--
                          Six fixed admin-cost categories plus an "Others"
                          bucket. Each bucket shows its own payments table +
                          total + Add Payment button. The total is the SUM
                          of payment amounts; the legacy
                          obj.adminitrativeCost.*CostAmount columns are no
                          longer shown here (still persisted in the DB for
                          backward compat). The 4 Sale-tab admin-cost
                          columns (Insurance, RegistrationFee, Roadtax,
                          BodyPrice) stay editable in the Sale tab and are
                          intentionally NOT shown as buckets here.
                        -->
                        <WidgetsAdminCostPaymentBucket
                          title="Duty Tax"
                          costType="DutyTax"
                          :payments="getAdminCostPaymentsByType('DutyTax')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Ap Amount"
                          costType="Ap"
                          :payments="getAdminCostPaymentsByType('Ap')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Forwarding"
                          costType="Forwarding"
                          :payments="getAdminCostPaymentsByType('Forwarding')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Int FS"
                          costType="IntFs"
                          :payments="getAdminCostPaymentsByType('IntFs')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Puspakom"
                          costType="Puspakom"
                          :payments="getAdminCostPaymentsByType('Puspakom')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Transfer Fee"
                          costType="TransferFee"
                          :payments="getAdminCostPaymentsByType('TransferFee')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                        <WidgetsAdminCostPaymentBucket
                          title="Others"
                          costType="Others"
                          :payments="getAdminCostPaymentsByType('Others')"
                          :isReadOnly="isReadOnly"
                          @add="onAddAdminCostPayment"
                          @update="onUpdateAdminCostPayment"
                          @remove="onRemoveAdminCostPayment"
                        />
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>

                <CTab v-if="!isNewApplication() && canViewDisbursement">
                  <template slot="title">
                    <i class="fas fa-money-check"></i> Disbursement
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CInput
                          label="Sub Company"
                          description="Company that used for AP"
                          horizontal
                          readonly
                          v-model="selectedApSubCompany.name"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchApSubCompany()"
                            >
                              Search
                            </CButton>
                          </template>
                        </CInput>
                        <CInput
                          label="Bank Account"
                          horizontal
                          readonly
                          v-model="selectedApBankAccount.name"
                        >
                          <template #append>
                            <CButton
                              color="primary"
                              @click="onSearchApBankAccount()"
                              :disabled="!selectedApSubCompany || !selectedApSubCompany.id"
                            >
                              Search
                            </CButton>
                          </template>
                          <template #description>
                            <small v-if="!selectedApSubCompany || !selectedApSubCompany.id" class="text-warning">
                              <i class="fas fa-exclamation-circle me-1"></i>
                              Please select Sub Company first
                            </small>
                          </template>
                        </CInput>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Disbursment Letters
                          </CCol>
                          <CCol sm="9">
                            <CRow>
                              <CCol>
                                <CButton
                                  size="sm"
                                  color="info"
                                  class="ml-1"
                                  @click="onOpenDisburstmentLetter()"
                                >
                                  Open
                                </CButton>
                              </CCol>
                            </CRow>
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>
                <CTab v-if="!isNewApplication() && canViewAdvertisement">
                  <template slot="title">
                    <i class="fas fa-bullhorn"></i> Advertisement
                  </template>
                  <CCard>
                    <CCardBody>
                      <CForm>
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            mudah.my?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="
                                obj.advertisement.mudahHadAdviertized
                              "
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Start
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeMudahStartDate"
                              @change="setMudahStartDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            End
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeMudahEndDate"
                              @change="setMudahEndDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>
                        <hr />
                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Car List?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="
                                obj.advertisement.carListHadAdviertized
                              "
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Start
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeCarListStartDate"
                              @change="setCarListStartDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            End
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeCarListEndDate"
                              @change="setCarListEndDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>
                        <hr />

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Cari Carz?
                          </CCol>
                          <CCol sm="9">
                            <CSwitch
                              class="mr-1"
                              color="primary"
                              :checked.sync="
                                obj.advertisement.cariCarzHadAdviertized
                              "
                              label-on="YES"
                              label-off="NO"
                            >
                            </CSwitch>
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            Start
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeCariCarzStartDate"
                              @change="setCariCarzStartDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>

                        <CRow form class="form-group">
                          <CCol tag="label" sm="3" class="col-form-label">
                            End
                          </CCol>
                          <CCol sm="9">
                            <input
                              type="date"
                              :value="computeCariCarzEndDate"
                              @change="setCariCarzEndDate"
                              class="mr-2"
                            />
                          </CCol>
                        </CRow>
                      </CForm>
                    </CCardBody>
                  </CCard>
                </CTab>
              </CTabs>
            </CCardBody>
            <CCardFooter>
              <CButton v-if="!isReadOnly" type="submit" size="sm" color="primary" @click="submit"
                ><CIcon name="cil-check-circle" /> Submit</CButton
              >
              <!-- <CButton
                class="ml-1"
                color="danger"
                :disabled="!isDirty"
                @click="reset"
              >
                Reset
              </CButton> -->
              <CButton class="ml-1" color="secondary" @click="cancel">
                Cancel
              </CButton>
            </CCardFooter>
          </CCard>
          <!-- Modal for maximizing image -->
          <CModal
            title="Maximized Image"
            :show.sync="isMaximizeImageModal"
            size="lg"
            @close="isMaximizeImageModal = false"
          >
            <CImg :src="currentImageUrl" class="maximized-image" />
          </CModal>
        </CCol>
        <CCol sm="12" md="12" lg="12" xl="12" xxl="3">
          <!-- Reusable remarks widget: composer + history + delete-own.
               See packages/front/src/views/widgets/WidgetsRemarks.vue -->
          <WidgetsRemarks
            v-if="obj && obj.id"
            entity-type="stock"
            :entity-id="obj.id"
            :remarks-list="reversedRemarksList"
            :is-read-only="isReadOnly"
            @added="resetObj"
            @deleted="resetObj"
            @toast="(t) => toast(t.header, t.message, t.color)"
          />
        </CCol>
      </CRow>
    </div>
    <div>
      <CModal title="Search for Brand" :show.sync="brandSearchPopup" size="xl">
        <CRow>
          <CCol sm="12">
            <CCard>
              <!-- <CCardHeader> <strong> Campaign </strong> List </CCardHeader> -->
              <CCardBody>
                <CDataTable
                  :items="brandList"
                  :fields="brandFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onBrandSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>

    <div>
      <CModal title="Search for Model" :show.sync="modelSearchPopup" size="xl">
        <CRow>
          <CCol sm="12">
            <CCard>
              <CCardBody>
                <CDataTable
                  :items="modelList"
                  :fields="modelFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onModelSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>

    <div>
      <CModal
        title="Search for Supplier"
        :show.sync="supplierSearchPopup"
        size="xl"
      >
        <CRow>
          <CCol sm="12">
            <CCard>
              <!-- <CCardHeader> <strong> Campaign </strong> List </CCardHeader> -->
              <CCardBody>
                <CDataTable
                  :items="supplierList"
                  :fields="supplierFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onSupplierSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>

    <div>
      <!-- Enhanced Customer Search Modal -->
      <CModal
        :show.sync="customerSearchPopup"
        size="xl"
        centered
      >
        <template #header>
          <h4 class="modal-title">
            <i class="fas fa-users me-2 text-primary"></i>
            Search for Customer
          </h4>
          <CButton
            color="secondary"
            variant="ghost"
            @click="customerSearchPopup = false"
            class="btn-close"
          >
            <i class="fas fa-times"></i>
          </CButton>
        </template>

        <!-- Header with actions -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <small class="text-muted">
              {{ customerList.length }} customers found
            </small>
          </div>
          <div>
            <CButton
              color="primary"
              size="sm"
              @click="refreshTableCustomer"
              :disabled="customerLoading"
              class="me-2"
            >
              <i class="fas fa-sync-alt me-1"></i>
              {{ customerLoading ? 'Loading...' : 'Refresh' }}
            </CButton>
          </div>
        </div>

        <!-- Customer List Table -->
        <div class="table-container" style="max-height: 400px; overflow-y: auto;">
          <CDataTable
            :items="customerList"
            :fields="enhancedCustomerFieldList"
            :column-filter="{ external: true, lazy: true }"
            :column-filter-value.sync="customerColumnFilterValue"
            :sorter="{ external: true, resetable: true }"
            :sorter-value.sync="customerSorterValue"
            hover
            :loading="customerLoading"
            responsive
            class="customer-table"
          >
            <!-- Name Column with Enhanced Display -->
            <template #name="{ item }">
              <td>
                <div class="customer-info">
                  <strong class="text-primary">{{ item.name || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">
                    <i class="fas fa-id-card me-1"></i>
                    {{ item.icNumber || 'No IC' }}
                  </small>
                </div>
              </td>
            </template>

            <!-- Contact Info Column -->
            <template #contact="{ item }">
              <td>
                <div class="contact-info">
                  <div v-if="item.phone" class="mb-1">
                    <i class="fas fa-phone me-1 text-success"></i>
                    <small>{{ item.phone }}</small>
                  </div>
                  <div v-if="item.email">
                    <i class="fas fa-envelope me-1 text-info"></i>
                    <small>{{ item.email }}</small>
                  </div>
                  <div v-if="!item.phone && !item.email">
                    <small class="text-muted">No contact info</small>
                  </div>
                </div>
              </td>
            </template>

            <!-- Action Column -->
            <template #actions="{ item }">
              <td class="text-center">
                <CButton
                  color="success"
                  size="sm"
                  @click="onCustomerSelected(item)"
                  class="select-btn"
                >
                  <i class="fas fa-check me-1"></i>
                  Select
                </CButton>
              </td>
            </template>

            <!-- Empty State -->
            <template #empty>
              <div class="text-center py-4">
                <i class="fas fa-users fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No customers found</h5>
                <p class="text-muted">
                  Try adjusting your column filters or click refresh to load customers
                </p>
                <CButton
                  color="primary"
                  variant="outline"
                  @click="refreshTableCustomer"
                >
                  <i class="fas fa-sync-alt me-2"></i>
                  Load Customers
                </CButton>
              </div>
            </template>
          </CDataTable>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="customerPages > 1">
          <small class="text-muted">
            Page {{ customerActivePage }} of {{ customerPages }}
          </small>
          <CPagination
            :pages="customerPages"
            :active-page.sync="customerActivePage"
            @update:active-page="refreshTableCustomer"
          />
        </div>

        <template #footer>
          <CButton
            color="secondary"
            @click="customerSearchPopup = false"
          >
            <i class="fas fa-times me-2"></i>
            Cancel
          </CButton>
          <CButton
            color="info"
            variant="outline"
            @click="onCreateNewCustomer"
            class="ms-2"
          >
            <i class="fas fa-plus me-2"></i>
            Create New Customer
          </CButton>
        </template>
      </CModal>

      <!-- Customer Creation Modal -->
      <CModal
        :show.sync="customerCreatePopup"
        size="lg"
        centered
        class="customer-create-modal"
      >
        <template #header>
          <h4 class="modal-title">
            <i class="fas fa-user-plus me-2 text-success"></i>
            Create New Customer
          </h4>
        </template>

        <CForm>
          <CInput
            label="Customer Name"
            placeholder="Enter customer name"
            v-model="newCustomer.name"
            required
            horizontal
          >
            <template #description>
              <small class="text-danger">* Required (minimum 2 characters)</small>
            </template>
          </CInput>

          <CInput
            label="IC Number"
            placeholder="Enter IC/Passport number"
            v-model="newCustomer.icNumber"
            horizontal
          />

          <CInput
            label="Phone"
            placeholder="Enter phone number"
            v-model="newCustomer.phone"
            horizontal
          />

          <CInput
            label="Email"
            placeholder="Enter email address"
            v-model="newCustomer.email"
            type="email"
            horizontal
          />

          <CTextarea
            label="Address"
            placeholder="Enter address"
            v-model="newCustomer.address"
            rows="3"
            horizontal
          />

          <CSelect
            label="Customer Type"
            :options="['Individual', 'Company']"
            :value.sync="newCustomer.customerType"
            placeholder="Select customer type"
            horizontal
            :disabled="isReadOnly"
          />
        </CForm>

        <template #footer>
          <CButton
            color="secondary"
            @click="onCancelCustomerCreation"
          >
            <i class="fas fa-times me-2"></i>
            Cancel
          </CButton>
          <CButton
            color="success"
            @click="onSaveNewCustomer"
            :disabled="creatingCustomer || !newCustomer.name || newCustomer.name.length < 2"
          >
            <CSpinner
              v-if="creatingCustomer"
              size="sm"
              class="me-2"
            />
            <i v-else class="fas fa-save me-2"></i>
            {{ creatingCustomer ? 'Creating...' : 'Create Customer' }}
          </CButton>
        </template>
      </CModal>
    </div>
    <div>
      <CModal
        title="Search for Salesman"
        :show.sync="salesmanSearchPopup"
        size="xl"
      >
        <CRow>
          <CCol sm="12">
            <CCard>
              <!-- <CCardHeader> <strong> Campaign </strong> List </CCardHeader> -->
              <CCardBody>
                <CDataTable
                  :items="salesmanList"
                  :fields="salesmanFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onSalesmanSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>
    <div>
      <CModal
        title="Search for Showroom"
        :show.sync="showroomSearchPopup"
        size="xl"
      >
        <CRow>
          <CCol sm="12">
            <CCard>
              <!-- <CCardHeader> <strong> Campaign </strong> List </CCardHeader> -->
              <CCardBody>
                <CDataTable
                  :items="showroomList"
                  :fields="showroomFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onShowroomSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>

      <CModal
  title="Change Stock No Confirmation"
  :show.sync="changeStockNoPopup"
  @update:show="onChangeStockNoConfirmation"
  :close-on-backdrop="false"
  :keyboard="false"
>
  <CForm>
    <CInput label="New Stock No" v-model="newStockNo" horizontal />
  </CForm>
  
  <!-- Custom footer to control the buttons -->
  <template #footer>
    <CButton 
      color="secondary" 
      @click="cancelChangeStockNo"
    >
      Cancel
    </CButton>
    <CButton 
      color="primary" 
      @click="confirmChangeStockNo"
      :disabled="!newStockNo || newStockNo.trim() === ''"
    >
      OK
    </CButton>
  </template>
</CModal>
    </div>

    <div>
      <CModal title="Search for Bank" :show.sync="bankSearchPopup" size="xl">
        <CRow>
          <CCol sm="12">
            <CCard>
              <!-- <CCardHeader> <strong> Campaign </strong> List </CCardHeader> -->
              <CCardBody>
                <CDataTable
                  :items="bankList"
                  :fields="bankFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onBankSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
        <template #footer>
          <CButton
            color="secondary"
            @click="bankSearchPopup = false"
          >
            Cancel
          </CButton>
          <CButton
            color="info"
            variant="outline"
            @click="onCreateNewBank"
            class="ms-2"
          >
            <i class="fas fa-plus me-2"></i>
            Create New Bank
          </CButton>
        </template>
      </CModal>
    </div>

    <div>
      <!-- Bank Creation Modal -->
      <CModal
        :show.sync="bankCreatePopup"
        size="lg"
        centered
        class="bank-create-modal"
      >
        <template #header>
          <h4 class="modal-title">
            <i class="fas fa-university me-2 text-success"></i>
            Create New Bank
          </h4>
        </template>

        <CForm>
          <CInput
            label="Bank Name"
            placeholder="Enter bank name"
            v-model="newBank.name"
            required
            horizontal
            description="Required (minimum 2 characters)"
          />
          <CTextarea
            label="Address"
            placeholder="Enter bank address"
            v-model="newBank.address"
            rows="3"
            horizontal
          />
          <CInput
            label="Phone"
            placeholder="Enter phone number"
            v-model="newBank.phone"
            horizontal
          />
          <CInput
            label="Email"
            placeholder="Enter email address"
            v-model="newBank.email"
            type="email"
            horizontal
          />
          <CInput
            label="Website"
            placeholder="Enter website URL"
            v-model="newBank.website"
            horizontal
          />
        </CForm>

        <template #footer>
          <CButton
            color="secondary"
            @click="onCancelBankCreation"
          >
            Cancel
          </CButton>
          <CButton
            color="success"
            @click="onSaveNewBank"
            :disabled="creatingBank || !newBank.name || newBank.name.length < 2"
          >
            <CSpinner v-if="creatingBank" size="sm" class="me-1" />
            <i v-else class="fas fa-check me-1"></i>
            Create Bank
          </CButton>
        </template>
      </CModal>
    </div>

    <div>
      <CModal
        title="Search for Company"
        :show.sync="subCompanySearchPopup"
        size="xl"
      >
        <CRow>
          <CCol sm="12">
            <CCard>
              <CCardBody>
                <CDataTable
                  :items="subCompanyList"
                  :fields="subApSubCompanyFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onSubCompanySelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>

    <div>
      <CModal
        :show.sync="bankAccountSearchPopup"
        size="xl"
      >
        <template #header>
          <div class="d-flex justify-content-between align-items-center w-100">
            <h5 class="mb-0">Search for Bank Account</h5>
            <CButton
              color="success"
              size="sm"
              @click="onOpenBankAccountAddModal()"
            >
              <i class="fas fa-plus me-1"></i>
              Add New
            </CButton>
          </div>
        </template>
        <CRow>
          <CCol sm="12">
            <CCard>
              <CCardBody>
                <CDataTable
                  :items="bankAccountList"
                  :fields="bankAccountFieldList"
                  column-filter
                  sorter
                  :loading="loading"
                >
                  <template #show_details="{ item, index }">
                    <td class="py-2">
                      <CButton
                        color="primary"
                        variant="outline"
                        square
                        size="sm"
                        @click="onBankAccountSelected(item, index)"
                      >
                        Select
                      </CButton>
                    </td>
                  </template>
                </CDataTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CModal>
    </div>

    <!-- Bank Account Add/Create Modal -->
    <div>
      <CModal
        :show.sync="bankAccountFormModal"
        size="lg"
      >
        <template #header>
          <h5 class="mb-0">
            <i class="fas fa-university me-2"></i>
            Create Bank Account
          </h5>
        </template>
        <CForm>
          <!-- Name -->
          <CRow class="mb-3">
            <CCol tag="label" sm="3" class="col-form-label">
              <strong>Name</strong>
              <span class="text-danger ms-1">*</span>
            </CCol>
            <CCol sm="9">
              <CInput
                v-model="bankAccountForm.name"
                placeholder="Enter bank account name"
              />
              <small class="form-text text-muted">
                <i class="fas fa-info-circle me-1"></i>
                Display name for this bank account
              </small>
            </CCol>
          </CRow>

          <!-- Address -->
          <CRow class="mb-3">
            <CCol tag="label" sm="3" class="col-form-label">
              <strong>Address</strong>
            </CCol>
            <CCol sm="9">
              <CTextarea
                v-model="bankAccountForm.address"
                placeholder="Enter bank address"
                rows="3"
              />
            </CCol>
          </CRow>

          <!-- Account Name -->
          <CRow class="mb-3">
            <CCol tag="label" sm="3" class="col-form-label">
              <strong>Account Name</strong>
            </CCol>
            <CCol sm="9">
              <CInput
                v-model="bankAccountForm.accountName"
                placeholder="Enter account holder name"
              />
            </CCol>
          </CRow>

          <!-- Account No -->
          <CRow class="mb-3">
            <CCol tag="label" sm="3" class="col-form-label">
              <strong>Account No</strong>
            </CCol>
            <CCol sm="9">
              <CInput
                v-model="bankAccountForm.accountNo"
                placeholder="Enter account number"
              />
            </CCol>
          </CRow>

          <!-- Account Type -->
          <CRow class="mb-3">
            <CCol tag="label" sm="3" class="col-form-label">
              <strong>Account Type</strong>
            </CCol>
            <CCol sm="9">
              <CInput
                v-model="bankAccountForm.accountType"
                placeholder="Enter account type (e.g., Current, Savings)"
              />
            </CCol>
          </CRow>
        </CForm>
        <template #footer>
          <CButton color="secondary" @click="onCancelBankAccountForm()">
            Cancel
          </CButton>
          <CButton color="primary" @click="onSaveBankAccount()">
            <i class="fas fa-save me-1"></i>
            Create Bank Account
          </CButton>
        </template>
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removePuspakomB7SlipDocumentWarningModal"
        @update:show="onRemovePuspakomB7SlipDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removePuspakomB2SlipDocumentWarningModal"
        @update:show="onRemovePuspakomB2SlipDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeJpjDaftarDocumentWarningModal"
        @update:show="onRemoveJpjDaftarDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeJpjHakMilikDocumentWarningModal"
        @update:show="onRemoveJpjHakMilikDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeJpjGeranDocumentWarningModal"
        @update:show="onRemoveJpjGeranDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeBolDocumentWarningModal"
        @update:show="onRemoveBolDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeK8DocumentWarningModal"
        @update:show="onRemoveK8DocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeK1DocumentWarningModal"
        @update:show="onRemoveK1DocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeLouDocumentWarningModal"
        @update:show="onRemoveLouDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeReceiptDocumentWarningModal"
        @update:show="onRemoveReceiptDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeInsuranceDocumentWarningModal"
        @update:show="onRemoveInsuranceDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeRoadTaxDocumentWarningModal"
        @update:show="onRemoveRoadTaxDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeReceiptEDaftarDocumentWarningModal"
        @update:show="onRemoveReceiptEDaftarDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeReceiptKastamDocumentWarningModal"
        @update:show="onRemoveReceiptKastamDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeExportCertificateDocumentWarningModal"
        @update:show="onRemoveExportCertificateDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Confirm Remove Document"
        color="warning"
        :show.sync="removeCustomerIcDocumentWarningModal"
        @update:show="onRemoveCustomerIcDocumentConfirmation"
      >
        Are you sure you want to delete?
      </CModal>
    </div>

    <div>
      <CModal
        title="Add/Edit Expense Item"
        :show.sync="addNewExpenseItemPopupState"
        size="xl"
        @update:show="onAddNewExpenseItemConfirmation"
      >
        <CForm>
          <CInput
            label="Date"
            type="date"
            v-model="newExpenseItem.expenseDate"
            horizontal
          />

          <CSelect
            label="Category"
            :options="expenseCategoryOptions"
            :value.sync="newExpenseItem.category"
            horizontal
            :disabled="isReadOnly"
          />

          <CInput
            label="Name"
            v-model="newExpenseItem.name"
            horizontal
          />

          <CInput
            label="Bill/Inv No"
            v-model="newExpenseItem.billOrInvoiceNo"
            horizontal
          />

          <CInput
            label="Amount"
            prepend="RM"
            v-model="newExpenseItem.amount"
            horizontal
          />

          <CInput
            label="Remarks"
            v-model="newExpenseItem.remarks"
            horizontal
          />

          <CRow class="mb-3">
            <CCol sm="3" class="text-sm-right">
              <label class="col-form-label">Upload File</label>
            </CCol>
            <CCol sm="9">
              <WidgetsUploadDocument
                :key="expenseUploadKey"
                @uploaded="expense_document_uploaded"
                :multiple="false"
              />
              <div v-if="newExpenseItem.document" class="mt-2">
                <small class="text-success">
                  <i class="fas fa-check-circle"></i>
                  {{ newExpenseItem.document.fileName || 'File uploaded' }}
                </small>
                <CButton
                  size="sm"
                  color="danger"
                  variant="ghost"
                  @click="removeExpenseDocument"
                  class="ml-2"
                >
                  <i class="fas fa-times"></i> Remove
                </CButton>
              </div>
            </CCol>
          </CRow>
        </CForm>
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Delete"
        color="warning"
        :show.sync="warningDeleteExpenseItemModal"
        @update:show="onDeleteExpenseItemConfirmation"
      >
        Are you sure you want to delete this
        {{ itemExpenseItemToDelete.name }} ?
      </CModal>
    </div>

    <div>
      <CModal
        title="Add/Edit Admin Item"
        :show.sync="addNewAdminItemPopupState"
        size="xl"
        @update:show="onAddNewAdminItemConfirmation"
      >
        <CForm>
          <CInput label="Name" v-model="newAdminItem.name" horizontal />

          <CInput
            label="Amount"
            prepend="RM"
            v-model="newAdminItem.amount"
            horizontal
          />
          <CInput label="Remarks" v-model="newAdminItem.remarks" horizontal />
        </CForm>
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Delete"
        color="warning"
        :show.sync="warningDeleteAdminItemModal"
        @update:show="onDeleteAdminItemConfirmation"
      >
        Are you sure you want to delete this
        {{ itemAdminItemToDelete.name }} ?
      </CModal>
    </div>

    <div>
      <CModal
        title="Add/Edit Arrival Item"
        :show.sync="addNewArrivalItemPopupState"
        size="xl"
        @update:show="onAddNewArrivalItemConfirmation"
      >
        <CForm>
          <CInput label="Name" v-model="newArrivalItem.name" horizontal />
          <CRow form class="form-group">
            <CCol tag="label" sm="3" class="col-form-label">
              Is Available?
            </CCol>
            <CCol sm="9">
              <CSwitch
                class="mr-1"
                color="primary"
                :checked.sync="newArrivalItem.isAvailable"
                label-on="YES"
                label-off="NO"
              >
              </CSwitch>
            </CCol>
          </CRow>

          <!-- <CInput
            label="Amount"
            prepend="RM"
            v-model="newArrivalItem.amount"
            horizontal
          /> -->
          <CInput label="Remarks" v-model="newArrivalItem.remarks" horizontal />
        </CForm>
      </CModal>
    </div>
    <div>
      <CModal
        title="Confirm Delete"
        color="warning"
        :show.sync="warningDeleteArrivalItemModal"
        @update:show="onDeleteArrivalItemConfirmation"
      >
        Are you sure you want to delete this
        {{ itemArrivalItemToDelete.name }} ?
      </CModal>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import StockApi from "../../lib/stockApi";
import StockStatusApi from "../../lib/stockStatusApi";
import VehicleTypeApi from "../../lib/vehicleTypeApi";
import BrandApi from "../../lib/brandApi";
import ModelApi from "../../lib/modelApi";
import SupplierApi from "../../lib/supplierApi";
import CustomerApi from "../../lib/customerApi";
import RoleApi from "../../lib/roleApi";
import ShowroomApi from "../../lib/showroomApi";
import ProfileApi from "../../lib/profileApi";
import BankApi from "../../lib/bankApi";
import SubCompanyApi from "../../lib/subCompanyApi";
import BankAccountApi from "../../lib/bankAccountApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage";
import WidgetsUploadDocument from "../widgets/WidgetsUploadDocument";
import WidgetsRemarks from "../widgets/WidgetsRemarks";
import WidgetsExpensePaymentBucket from "../widgets/WidgetsExpensePaymentBucket.vue";
import WidgetsAdminCostPaymentBucket from "../widgets/WidgetsAdminCostPaymentBucket.vue";
import WidgetsPurchasePaymentBucket from "../widgets/WidgetsPurchasePaymentBucket.vue";
import WidgetsSalePaymentBucket from "../widgets/WidgetsSalePaymentBucket.vue";

import { required, minLength } from "vuelidate/lib/validators";
import { decimal } from "@/utils/validators";
import moment from "moment";
import { formatMalaysiaDate, formatMalaysiaDateTime } from "@/utils/dateUtil";

// Tab key registry — order MUST match the CTab order in the template.
// Keys are URL-safe strings used in the ?tab= query parameter.
const TAB_DEFINITIONS = [
  { key: 'stock-info',        visibleWhen: vm => vm.canViewStockInfo },
  { key: 'vehicle-info',      visibleWhen: vm => !vm.isNewApplication() && vm.canViewVehicleInfo },
  { key: 'purchase',          visibleWhen: vm => !vm.isNewApplication() && vm.canViewPurchase },
  { key: 'import',            visibleWhen: vm => !vm.isNewApplication() && vm.canViewImport },
  { key: 'clearance',         visibleWhen: vm => !vm.isNewApplication() && vm.canViewClearance },
  { key: 'sale',              visibleWhen: vm => !vm.isNewApplication() && vm.canViewSale },
  { key: 'pricing',           visibleWhen: vm => !vm.isNewApplication() && vm.canViewPricing },
  { key: 'arrival-checklist', visibleWhen: vm => !vm.isNewApplication() && vm.canViewArrivalChecklist },
  { key: 'registration',      visibleWhen: vm => !vm.isNewApplication() && vm.canViewRegistration },
  { key: 'expenses',          visibleWhen: vm => !vm.isNewApplication() && vm.canViewExpenses },
  { key: 'admin-cost',        visibleWhen: vm => !vm.isNewApplication() && vm.canViewAdministrativeCost },
  { key: 'disbursement',      visibleWhen: vm => !vm.isNewApplication() && vm.canViewDisbursement },
  { key: 'advertisement',     visibleWhen: vm => !vm.isNewApplication() && vm.canViewAdvertisement },
];

const stockStatusHistoryFieldList = [
  {
    key: "status_name",
    _style: "min-width:100px;",
    label: "Name",
  },
  {
    key: "date_time",
    label: "Date",
  },
  {
    key: "profile_name",
    label: "Update By",
  },
];

const brandList = [];
const brandFieldList = [
  { key: "name" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const modelList = [];
const modelFieldList = [
  { key: "name" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const supplierList = [];
const supplierFieldList = [
  { key: "name" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const customerList = [];
const salesmanList = [];
const showroomList = [];
const customerFieldList = [
  { key: "name" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

// Enhanced customer field list for the new modal
const enhancedCustomerFieldList = [
  { 
    key: "name", 
    label: "Customer", 
    _style: "min-width:200px",
    sorter: true 
  },
  { 
    key: "contact", 
    label: "Contact Info", 
    _style: "min-width:180px",
    sorter: false,
    filter: false 
  },
  {
    key: "actions",
    label: "Action",
    _style: "width:120px",
    sorter: false,
    filter: false,
  },
];

const salesmanFieldList = [
  { key: "fullName" },
  { key: "role", label: "Role" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];
const showroomFieldList = [
  { key: "lotNo" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const bankList = [];
const bankFieldList = [
  { key: "name" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

const puspakomB7SlipDocumentList = [];
const puspakomB7SlipDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const puspakomB2SlipDocumentList = [];
const puspakomB2SlipDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const jpjDaftarDocumentList = [];
const jpjDaftarDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const jpjHakMilikDocumentList = [];
const jpjHakMilikDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const jpjGeranDocumentList = [];
const jpjGeranDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const bolDocumentList = [];
const bolDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const k8DocumentList = [];
const k8DocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const k1DocumentList = [];
const k1DocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const louDocumentList = [];
const louDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const receiptDocumentList = [];
const receiptDocumentFieldList = [
  {
    key: "document_link",
    label: "Document",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const administrativeCostList = [];
const administrativeCostFieldList = [
  {
    key: "name",
    sorter: false,
    filter: false,
  },
  {
    key: "amount",
    sorter: false,
    filter: false,
  },
  {
    key: "remarks",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];
const expensesList = [];
const expensesFieldList = [
  {
    key: "expenseDate",
    label: "Date",
    sorter: false,
    filter: false,
  },
  {
    key: "category",
    label: "Category",
    sorter: false,
    filter: false,
  },
  {
    key: "name",
    sorter: false,
    filter: false,
  },
  {
    key: "billOrInvoiceNo",
    label: "Bill/Inv No",
    sorter: false,
    filter: false,
  },
  {
    key: "amount",
    sorter: false,
    filter: false,
  },
  {
    key: "remarks",
    sorter: false,
    filter: false,
  },
  {
    key: "document",
    label: "File",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

const arrivalChecklistList = [];
const arrivalChecklistFieldList = [
  {
    key: "name",
    sorter: false,
    filter: false,
  },
  {
    key: "isAvailable",
    sorter: false,
    filter: false,
  },
  {
    key: "remarks",
    sorter: false,
    filter: false,
  },
  {
    key: "remove",
    label: "",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "Stock",
  components: {
    WidgetsUploadImage,
    WidgetsUploadDocument,
    WidgetsRemarks,
    WidgetsExpensePaymentBucket,
    WidgetsAdminCostPaymentBucket,
    WidgetsPurchasePaymentBucket,
    WidgetsSalePaymentBucket,
  },

  data: () => {
    return {
      activeTab: 0,
      isDataLoading: true,
      warningDeleteArrivalItemModal: false,
      itemArrivalItemToDelete: {},
      addNewArrivalItemPopupState: false,
      newArrivalItem: {
        id: null,
        name: "",
        isAvailable: false,
        remarks: "",
      },

      warningDeleteAdminItemModal: false,
      itemAdminItemToDelete: {},
      addNewAdminItemPopupState: false,
      newAdminItem: {
        id: null,
        name: "",
        amount: 0,
        remarks: "",
      },

      warningDeleteExpenseItemModal: false,
      itemExpenseItemToDelete: {},
      addNewExpenseItemPopupState: false,
      expenseUploadKey: 0,
      newExpenseItem: {
        id: null,
        name: "",
        amount: 0,
        remarks: "",
      },

      arrivalStates: [],
      selectedArrivalState: null,

      isMaximizeImageModal: false, // State for modal visibility
      currentImageUrl: "", // State to store the URL of the image to be maximized

      newAdministrativeCostItem: {
        name: "",
        amount: 0,
        remarks: "",
      },
      administrativeCostToDelete: null,
      removeAdministrativeCostWarningModal: false,

      administrativeCostList: administrativeCostList.map((item, id) => {
        return { ...item, id };
      }),
      administrativeCostFieldList,

      //
      newExpenseItem: {
        id: null,
        expenseDate: "2001-01-01",
        category: "",
        name: "",
        billOrInvoiceNo: "",
        amount: 0,
        remarks: "",
        documentId: null,
        document: null
      },
      expenseCategoryOptions: [
        { value: "", text: "Select Category" },
        { value: "Service", text: "Service" },
        { value: "Interior", text: "Interior" },
        { value: "Paint", text: "Paint" },
        { value: "Tyre", text: "Tyre" },
        { value: "Rental", text: "Rental" }
      ],
      expensesToDelete: null,
      removeExpensesWarningModal: false,

      expensesList: expensesList.map((item, id) => {
        return { ...item, id };
      }),
      expensesFieldList,

      //

      newCheckListItem: {
        name: "",
        isAvailable: false,
        remarks: "",
      },
      arrivalChecklistToDelete: null,
      removeArrivalChecklistWarningModal: false,

      arrivalChecklistList: arrivalChecklistList.map((item, id) => {
        return { ...item, id };
      }),
      arrivalChecklistFieldList,

      //

      louDocumentToDelete: null,
      removeLouDocumentWarningModal: false,

      louDocumentList: louDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      louDocumentFieldList,

      receiptDocumentToDelete: null,
      removeReceiptDocumentWarningModal: false,

      receiptDocumentList: receiptDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      receiptDocumentFieldList,

      //
      k8DocumentToDelete: null,
      removeK8DocumentWarningModal: false,

      k8DocumentList: k8DocumentList.map((item, id) => {
        return { ...item, id };
      }),
      k8DocumentFieldList,
      //

      //
      k1DocumentToDelete: null,
      removeK1DocumentWarningModal: false,

      k1DocumentList: k1DocumentList.map((item, id) => {
        return { ...item, id };
      }),
      k1DocumentFieldList,
      //

      bolDocumentToDelete: null,
      removeBolDocumentWarningModal: false,

      bolDocumentList: bolDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      bolDocumentFieldList,

      //
      puspakomB7SlipDocumentToDelete: null,
      removePuspakomB7SlipDocumentWarningModal: false,

      puspakomB7SlipDocumentList: puspakomB7SlipDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      puspakomB7SlipDocumentFieldList,
      //
      puspakomB2SlipDocumentToDelete: null,
      removePuspakomB2SlipDocumentWarningModal: false,

      puspakomB2SlipDocumentList: puspakomB2SlipDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      puspakomB2SlipDocumentFieldList,

      //
      jpjDaftarDocumentToDelete: null,
      removeJpjDaftarDocumentWarningModal: false,

      jpjDaftarDocumentList: jpjDaftarDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      jpjDaftarDocumentFieldList,

      //
      jpjHakMilikDocumentToDelete: null,
      removeJpjHakMilikDocumentWarningModal: false,

      jpjHakMilikDocumentList: jpjHakMilikDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      jpjHakMilikDocumentFieldList,

      //
      jpjGeranDocumentToDelete: null,
      removeJpjGeranDocumentWarningModal: false,

      jpjGeranDocumentList: jpjGeranDocumentList.map((item, id) => {
        return { ...item, id };
      }),
      jpjGeranDocumentFieldList,

      // Insurance Documents
      insuranceDocumentToDelete: null,
      removeInsuranceDocumentWarningModal: false,
      insuranceDocumentList: [],
      insuranceDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      // RoadTax Documents
      roadTaxDocumentToDelete: null,
      removeRoadTaxDocumentWarningModal: false,
      roadTaxDocumentList: [],
      roadTaxDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      // ReceiptEDaftar Documents
      receiptEDaftarDocumentToDelete: null,
      removeReceiptEDaftarDocumentWarningModal: false,
      receiptEDaftarDocumentList: [],
      receiptEDaftarDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      // ReceiptKastam Documents
      receiptKastamDocumentToDelete: null,
      removeReceiptKastamDocumentWarningModal: false,
      receiptKastamDocumentList: [],
      receiptKastamDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      // ExportCertificate Documents
      exportCertificateDocumentToDelete: null,
      removeExportCertificateDocumentWarningModal: false,
      exportCertificateDocumentList: [],
      exportCertificateDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      // CustomerIc Documents
      customerIcDocumentToDelete: null,
      removeCustomerIcDocumentWarningModal: false,
      customerIcDocumentList: [],
      customerIcDocumentFieldList: [
        { key: "document_link", label: "Document" },
        { key: "remove" },
      ],

      //Model
      modelApi: new ModelApi(),
      modelSearchPopup: false,
      modelList: modelList.map((item, id) => {
        return { ...item, id };
      }),
      modelFieldList,
      selectedModel: {
        id: null,
        name: "",
      },
      //Model

      //Brand
      brandApi: new BrandApi(),
      brandSearchPopup: false,
      brandList: brandList.map((item, id) => {
        return { ...item, id };
      }),
      brandFieldList,
      selectedBrand: {
        id: null,
        name: "",
      },
      //Brand

      //Sub Company
      subCompanyApi: new SubCompanyApi(),
      subCompanySearchPopup: false,
      subCompanyList: [],
      subApSubCompanyFieldList: [
        { key: "name" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      selectedApSubCompany: {
        id: null,
        name: "",
      },
      //Brand

      //Bank Account
      bankAccountApi: new BankAccountApi(),
      bankAccountSearchPopup: false,
      bankAccountList: [],
      bankAccountFieldList: [
        { key: "name" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      selectedApBankAccount: {
        id: null,
        name: "",
      },

      // Bank Account Form Modal
      bankAccountFormModal: false,
      bankAccountForm: {
        name: "",
        address: "",
        accountName: "",
        accountNo: "",
        accountType: "",
        subCompanyId: null,
      },

      //Bank Account
      //Supplier
      supplierApi: new SupplierApi(),
      supplierSearchPopup: false,
      supplierList: supplierList.map((item, id) => {
        return { ...item, id };
      }),
      supplierFieldList,
      selectedSupplier: {
        id: null,
        name: "",
      },

      customerApi: new CustomerApi(),

      changeStockNoPopup: false,
      newStockNo: "",

      //Showroom
      showroomApi: new ShowroomApi(),
      showroomList: showroomList.map((item, id) => {
        return { ...item, id };
      }),
      showroomSearchPopup: false,
      showroomFieldList,
      selectedShowroom: {
        id: null,
        lotNo: "",
      },

      roleApi: new RoleApi(),
      profileApi: new ProfileApi(),
      customerSearchPopup: false,
      salesmanSearchPopup: false,
      customerList: customerList.map((item, id) => {
        return { ...item, id };
      }),
      salesmanList: salesmanList.map((item, id) => {
        return { ...item, id };
      }),
      customerFieldList,
      enhancedCustomerFieldList,
      salesmanFieldList,
      
      // Enhanced customer search properties
      customerColumnFilterValue: {},
      customerSorterValue: { column: null, asc: true },
      customerActivePage: 1,
      customerItemsPerPage: 10,
      customerPages: 1,
      customerLoading: false,
      customerSearchTimeout: null,

      selectedCustomer: {
        id: null,
        name: "",
      },

      customerCreatePopup: false,
      newCustomer: {
        customerType: "",
        name: "",
        icNumber: "",
        tinNumber: "",
        address: "",
        email: "",
        phone: "",
      },
      creatingCustomer: false,

      selectedSalesman: {
        id: null,
        fullName: "",
      },

      bankApi: new BankApi(),
      bankSearchPopup: false,
      bankList: bankList.map((item, id) => {
        return { ...item, id };
      }),
      bankFieldList,
      selectedBank: {
        id: null,
        name: "",
      },
      bankCreatePopup: false,
      newBank: {
        name: "",
        address: "",
        phone: "",
        email: "",
        website: "",
      },
      creatingBank: false,

      //Brand

      stockStatuses: [],
      selectedStockStatusId: null,

      vehicleTypes: [],
      selectedVehicleTypeId: null,

      infoList: [],
      api: new StockApi(),
      stockStatusApi: new StockStatusApi(),
      vehicleTypeApi: new VehicleTypeApi(),
      obj: {
        stockNo: "",
        vehicle: {
          chasisNo: "",
          codeModel: "",
        },
        purchase: {
          supplier: {
            name: "",
          },
        },

        import: {
          shipName: "",
        },
        clearance: {
          approvedPermitNo: "",
          lotNo: "",
        },
        sale: {
          customer: {
            name: "",
          },
          salesmanId: null,
          louPurchasePrice: 0,
          warranty: "",
          warrantyType: "",
          depositAmount: 0,
          loan: {
            depositAmount: 0,
            bank: {
              name: "",
            },
          },
        },
        // showroomId: null,
        // showRoom: {
        //   id: null,
        //   lotNo: "",
        // },
        pricing: {
          recommendedSalePrice: 0,
          minimumSalePrice: 0,
        },
        adminitrativeCost: {
          apCostAmount: 0,
          dutyCostAmount: 0,
          forwardingCostAmount: 0,
          serviceEzCareCostAmount: 0,
          interiorCostAmount: 0,
          paintCostAmount: 0,
          tyreCostAmount: 0,
          rentalCostAmount: 0,
          intFsCostAmount: 0,
          insuranceAmount: 0,
          insuranceCompanyName: "",
          puspakomRegRoadTax: 0,
          puspakomAmount: 0,
          transferFeeAmount: 0,
          registrationFeeAmount: 0,
          roadtaxAmount: 0,
          bodyPrice: 0,
        },
        registration: {
          racNo: "",
        },
      },
      // categoryList: categoryList.map((item, index) => {
      //   return { ...item, index };
      // }),
      stockStatusHistoryFieldList,

      loading: false,
      submitted: false,
      remark: "",
      bookingStartDate: moment().format("YYYY-MM-DD"),
      bookingEndDate: "2001-01-01",
      louStartDate: moment().format("YYYY-MM-DD"),
      louEndDate: "2001-01-01",

      mudahStartDate: "2001-01-01",
      mudahEndDate: "2001-01-01",
      carListStartDate: "2001-01-01",
      carListEndDate: "2001-01-01",

      cariCarzStartDate: "2001-01-01",
      cariCarzEndDate: "2001-01-01",

      estimateDateOfDeparture: Date(),
      estimateDateOfArrival: "2001-01-01",
      dateOfBillOfLading: "2001-01-01",
      vehicleRegistrationDate: "2001-01-01",
      systemRegistrationDate: Date(),
    };
  },
  mounted() {
    var self = this;
    self.fetchArrivalStates();
    self.refreshStockStatus();
    self.refreshVehicleType();
    self.resetObj();

    // Restore active tab from URL query param on page load/refresh
    self.syncTabFromUrl();
  },
  validations: {
    obj: {
      stockNo: {
        required,
        minLength: minLength(2),
      },
      sale: {
        louPurchasePrice: {
          decimal
        },
        loan: {
          approvedLoanAmount: {
            decimal
          }
        }
      },
      adminitrativeCost: {
        insuranceAmount: {
          decimal
        },
        registrationFeeAmount: {
          decimal
        },
        roadtaxAmount: {
          decimal
        },
        dutyCostAmount: {
          decimal
        },
        apCostAmount: {
          decimal
        },
        forwardingCostAmount: {
          decimal
        },
        intFsCostAmount: {
          decimal
        },
        puspakomAmount: {
          decimal
        },
        transferFeeAmount: {
          decimal
        }
      },
      expense: {
        serviceEzCareCostAmount: {
          decimal
        },
        interiorCostAmount: {
          decimal
        },
        paintCostAmount: {
          decimal
        },
        tyreCostAmount: {
          decimal
        },
        rentalCostAmount: {
          decimal
        }
      },
      pricing: {
        recommendedSalePrice: {
          decimal
        },
        minimumSalePrice: {
          decimal
        }
      },
      purchase: {
        vehiclePriceSupplierCurrency: {
          decimal
        },
        vehiclePriceLocalCurrency: {
          decimal
        },
        bodyPriceLocalCurrency: {
          decimal
        }
      },
      vehicle: {
        chasisNo: {
          minLength: minLength(3)
        },
        engineNo: {
          minLength: minLength(3)
        }
      },
      registration: {
        vehicleRegistrationNumber: {
          minLength: minLength(3)
        }
      }
    },
  },
  computed: {
    // Ordered list of currently visible tab keys (for key<->index mapping)
    visibleTabs() {
      return TAB_DEFINITIONS.filter(t => t.visibleWhen(this)).map(t => t.key);
    },

    stokHeader() {
      return this.obj.stockNo + " - " + this.getBrandName() + " " + this.getModelName() + " " + this.getYear();
    },

    reversedRemarksList() {
      return this.obj.remarksList;
    },

    sortedVehiclePhotos() {
      // Sort the images by their Position property
      return this.obj.vehicle.vehiclePhotoList.sort(
        (a, b) => a.position - b.position
      );
    },
    computedAdministrativeCostList() {
      return this.administrativeCostList.map((item) => {
        return {
          ...item,
        };
      });
    },
    computedExpensesList() {
      return this.expensesList.map((item) => {
        return {
          ...item,
        };
      });
    },
    purchasePayments() {
      return (this.obj && this.obj.purchase && this.obj.purchase.purchasePayments) || [];
    },
    salePayments() {
      return (this.obj && this.obj.sale && this.obj.sale.salePayments) || [];
    },
    computedArrivalChecklistList() {
      return this.arrivalChecklistList.map((item) => {
        return {
          ...item,
        };
      });
    },
    computedLouDocumentList() {
      return this.louDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedK8DocumentList() {
      return this.k8DocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedK1DocumentList() {
      return this.k1DocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedPuspakomB7SlipDocumentList() {
      return this.puspakomB7SlipDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedPuspakomB2SlipDocumentList() {
      return this.puspakomB2SlipDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedJpjDaftarDocumentList() {
      return this.jpjDaftarDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedJpjHakMilikDocumentList() {
      return this.jpjHakMilikDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },
    computedJpjGeranDocumentList() {
      return this.jpjGeranDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedInsuranceDocumentList() {
      return this.insuranceDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedRoadTaxDocumentList() {
      return this.roadTaxDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedReceiptEDaftarDocumentList() {
      return this.receiptEDaftarDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedReceiptKastamDocumentList() {
      return this.receiptKastamDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedExportCertificateDocumentList() {
      return this.exportCertificateDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedCustomerIcDocumentList() {
      return this.customerIcDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedSellingPrice: {
      get() {
        const louPurchasePrice = parseFloat(this.obj.sale?.louPurchasePrice) || 0;
        const roadTax = parseFloat(this.obj.adminitrativeCost?.roadtaxAmount) || 0;
        const insurance = parseFloat(this.obj.adminitrativeCost?.insuranceAmount) || 0;
        const registrationFee = parseFloat(this.obj.adminitrativeCost?.registrationFeeAmount) || 0;

        const calculatedPrice = louPurchasePrice - roadTax - insurance - registrationFee;
        return calculatedPrice.toFixed(2);
      },
      set(value) {
        // Setter is required for v-model but we don't allow manual editing
        // The value is computed automatically
      }
    },

    computedDepositAmount: {
      get() {
        const louPurchasePrice = parseFloat(this.obj.sale?.louPurchasePrice) || 0;
        const approvedLoanAmount = parseFloat(this.obj.sale?.loan?.approvedLoanAmount) || 0;

        const calculatedDeposit = louPurchasePrice - approvedLoanAmount;
        return calculatedDeposit.toFixed(2);
      },
      set(value) {
        // Setter is required for v-model but we don't allow manual editing
        // The value is computed automatically
      }
    },

    computedReceiptDocumentList() {
      return this.receiptDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computedBolDocumentList() {
      return this.bolDocumentList.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computeBookingStartDate() {
      return this.bookingStartDate;
    },
    computeBookingEndDate() {
      return this.bookingEndDate;
    },
    computeLouStartDate() {
      return this.louStartDate;
    },
    computeLouEndDate() {
      return this.louEndDate;
    },

    computeMudahStartDate() {
      return this.mudahStartDate;
    },
    computeMudahEndDate() {
      return this.mudahEndDate;
    },

    computeCarListStartDate() {
      return this.carListStartDate;
    },
    computeCarListEndDate() {
      return this.carListEndDate;
    },

    computeCariCarzStartDate() {
      return this.cariCarzStartDate;
    },
    computeCariCarzEndDate() {
      return this.cariCarzEndDate;
    },

    computeEstimateDateOfDeparture() {
      return moment(this.estimateDateOfDeparture).format("YYYY-MM-DDTHH:mm");
    },
    computeEstimateDateOfArrival() {
      return this.estimateDateOfArrival;
    },
    computeDateOfBillOfLading() {
      return this.dateOfBillOfLading;
    },

    computeVehicleRegistrationDate() {
      return this.vehicleRegistrationDate;
    },

    computeSystemRegistrationDate() {
      return moment(this.systemRegistrationDate).format("YYYY-MM-DDTHH:mm");
    },

    vehicleYear() {
      var year = [];
      year.push("");
      for (let i = 2000; i < 2050; i++) {
        year.push(i.toString());
      }
      return year;
    },
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },

    // Submodule permission checks for tabs
    canViewStockInfo() {
      const canView = this.$auth.hasSubModulePermission('STOCK_INFO', 'CanView');
      console.log('canViewStockInfo:', canView);
      return canView;
    },
    canViewVehicleInfo() {
      return this.$auth.hasSubModulePermission('VEHICLE_INFO', 'CanView');
    },
    canViewPurchase() {
      return this.$auth.hasSubModulePermission('PURCHASE', 'CanView');
    },
    canViewImport() {
      return this.$auth.hasSubModulePermission('IMPORT', 'CanView');
    },
    canViewClearance() {
      return this.$auth.hasSubModulePermission('CLEARANCE', 'CanView');
    },
    canViewSale() {
      return this.$auth.hasSubModulePermission('SALE', 'CanView');
    },
    canViewPricing() {
      return this.$auth.hasSubModulePermission('PRICING', 'CanView');
    },
    canViewArrivalChecklist() {
      return this.$auth.hasSubModulePermission('ARRIVAL_CHECKLIST', 'CanView');
    },
    canViewRegistration() {
      return this.$auth.hasSubModulePermission('REGISTRATION', 'CanView');
    },
    canViewExpenses() {
      return this.$auth.hasSubModulePermission('EXPENSES', 'CanView');
    },
    canViewAdministrativeCost() {
      return this.$auth.hasSubModulePermission('ADMINISTRATIVE_COST', 'CanView');
    },
    canViewDisbursement() {
      return this.$auth.hasSubModulePermission('DISBURSEMENT', 'CanView');
    },
    canViewAdvertisement() {
      const canView = this.$auth.hasSubModulePermission('STOCK_ADVERTISEMENT', 'CanView');
      console.log('canViewAdvertisement:', canView);
      return canView;
    },

    // Submodule permission checks for updates
    canUpdateStockInfo() {
      return this.$auth.hasSubModulePermission('STOCK_INFO', 'CanUpdate');
    },
    canUpdateVehicleInfo() {
      return this.$auth.hasSubModulePermission('VEHICLE_INFO', 'CanUpdate');
    },
    canUpdatePurchase() {
      return this.$auth.hasSubModulePermission('PURCHASE', 'CanUpdate');
    },
    canUpdateImport() {
      return this.$auth.hasSubModulePermission('IMPORT', 'CanUpdate');
    },
    canUpdateClearance() {
      return this.$auth.hasSubModulePermission('CLEARANCE', 'CanUpdate');
    },
    canUpdateSale() {
      return this.$auth.hasSubModulePermission('SALE', 'CanUpdate');
    },
    canUpdatePricing() {
      return this.$auth.hasSubModulePermission('PRICING', 'CanUpdate');
    },
    canUpdateArrivalChecklist() {
      return this.$auth.hasSubModulePermission('ARRIVAL_CHECKLIST', 'CanUpdate');
    },
    canUpdateRegistration() {
      return this.$auth.hasSubModulePermission('REGISTRATION', 'CanUpdate');
    },
    canUpdateExpenses() {
      return this.$auth.hasSubModulePermission('EXPENSES', 'CanUpdate');
    },
    canUpdateAdministrativeCost() {
      return this.$auth.hasSubModulePermission('ADMINISTRATIVE_COST', 'CanUpdate');
    },
    canUpdateDisbursement() {
      return this.$auth.hasSubModulePermission('DISBURSEMENT', 'CanUpdate');
    },
    canUpdateAdvertisement() {
      return this.$auth.hasSubModulePermission('STOCK_ADVERTISEMENT', 'CanUpdate');
    },

    // Overall readonly check - if user cannot update the current visible tab, make it readonly
    isReadOnly() {
      // For new applications, allow editing
      if (this.isNewApplication()) {
        return false;
      }
      // For existing stocks, check if user can update at least one visible submodule
      return !(
        this.canUpdateStockInfo ||
        this.canUpdateVehicleInfo ||
        this.canUpdatePurchase ||
        this.canUpdateImport ||
        this.canUpdateClearance ||
        this.canUpdateSale ||
        this.canUpdatePricing ||
        this.canUpdateArrivalChecklist ||
        this.canUpdateRegistration ||
        this.canUpdateExpenses ||
        this.canUpdateAdministrativeCost ||
        this.canUpdateDisbursement ||
        this.canUpdateAdvertisement
      );
    },
  },
  methods: {
    getBrandName() {
      try {
        return this.obj.vehicle.brand.name;
      } catch (error) {
        return "???";
      }
    },
    getModelName() {
      try {
        return this.obj.vehicle.model.name;
      } catch (error) {
        return "???";
      }
    },
    getYear() {
      try {
        return this.obj.vehicle.year;
      } catch (error) {
        return "???";
      }
    },
    onEditExpenseItem(item) {
      console.log('onEditExpenseItem - item:', item);
      console.log('onEditExpenseItem - item.document:', item.document);
      this.newExpenseItem.id = item.id;
      this.newExpenseItem.expenseDate = item.expenseDate
        ? moment(item.expenseDate).format("YYYY-MM-DD")
        : "2001-01-01";
      this.newExpenseItem.category = item.category || "";
      this.newExpenseItem.name = item.name || "";
      this.newExpenseItem.billOrInvoiceNo = item.billOrInvoiceNo || "";
      this.newExpenseItem.amount = item.amount || 0;
      this.newExpenseItem.remarks = item.remarks || "";
      this.newExpenseItem.documentId = item.documentId || null;
      this.newExpenseItem.document = item.document || null;
      console.log('onEditExpenseItem - newExpenseItem.document:', this.newExpenseItem.document);
      this.expenseUploadKey++; // Reset upload component
      this.addNewExpenseItemPopupState = true;
    },
    onDeleteExpenseItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        this.api
          .removeExpenseItem(this.itemExpenseItemToDelete.id)
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
      self.itemExpenseItemToDelete = {};
    },

    showDeleteExpenseItemConfirmation(item) {
      var self = this;
      self.itemExpenseItemToDelete = item;
      self.warningDeleteExpenseItemModal = true;
    },

    onAddNewExpenseItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        console.log(self.newExpenseItem);

        if (self.newExpenseItem.id) {
          this.api
            .updateExpenseItem(self.obj.id, self.newExpenseItem)
            .then((response) => {
              self.toast("Success", "Expense Item Updated", "success");

              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        } else {
          console.log(self.newExpenseItem);
          this.api
            .addExpenseItem(self.obj.id, self.newExpenseItem)
            .then((response) => {
              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        }
      }
    },

    addExpenseItemPopup() {
      this.resetNewExpenseItem();
      this.expenseUploadKey++; // Reset upload component
      this.addNewExpenseItemPopupState = true;
    },
    resetNewExpenseItem() {
      this.newExpenseItem.id = null;
      this.newExpenseItem.expenseDate = "2001-01-01";
      this.newExpenseItem.category = "";
      this.newExpenseItem.name = "";
      this.newExpenseItem.billOrInvoiceNo = "";
      this.newExpenseItem.amount = 0;
      this.newExpenseItem.remarks = "";
      this.newExpenseItem.documentId = null;
      this.newExpenseItem.document = null;
    },

    expense_document_uploaded(data) {
      console.log('expense_document_uploaded:', data);
      if (data && data.uploadedFiles && data.uploadedFiles.length > 0) {
        this.newExpenseItem.document = data.uploadedFiles[0];
        this.newExpenseItem.documentId = data.uploadedFiles[0].id;
        console.log('Document set:', this.newExpenseItem.document);
      }
    },

    removeExpenseDocument() {
      this.newExpenseItem.document = null;
      this.newExpenseItem.documentId = null;
    },

    getExpenseDate(item) {
      if (item.expenseDate) {
        return formatMalaysiaDate(item.expenseDate);
      }
      return "N/A";
    },

    getCategoryBadgeColor(category) {
      const colors = {
        Service: "primary",
        Interior: "success",
        Paint: "warning",
        Tyre: "info",
        Rental: "secondary"
      };
      return colors[category] || "dark";
    },

    getDocumentUrl(document) {
      if (document && document.id) {
        return apiUrl + `Documents/File/${document.id}`;
      }
      return "#";
    },

    // ===== ExpensePayment handlers (partial payments per fixed category).
    // The bucket widget computes its own total from the `payments` array we
    // pass in; after any CRUD we reload the stock via resetObj() so all
    // bucket totals refresh.
    getPaymentsByType(expenseType) {
      const list = (this.obj && this.obj.expense && this.obj.expense.expensePayments) || [];
      return list.filter(p => p.expenseType === expenseType);
    },

    onAddExpensePayment(payload) {
      const self = this;
      const stockId = self.$route.params.id;
      if (!stockId) return;
      self.api
        .addExpensePayment(stockId, payload)
        .then(() => {
          self.toast("Success", "Payment added", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onUpdateExpensePayment({ id, payload }) {
      const self = this;
      self.api
        .updateExpensePayment(id, payload)
        .then(() => {
          self.toast("Success", "Payment updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onRemoveExpensePayment(paymentId) {
      const self = this;
      self.api
        .removeExpensePayment(paymentId)
        .then(() => {
          self.toast("Success", "Payment removed", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    // ===== AdminitrativeCostPayment handlers (partial payments per fixed
    // admin-cost category). Mirror the ExpensePayment handlers exactly.
    getAdminCostPaymentsByType(costType) {
      const list = (this.obj && this.obj.adminitrativeCost && this.obj.adminitrativeCost.adminitrativeCostPayments) || [];
      return list.filter(p => p.costType === costType);
    },

    onAddAdminCostPayment(payload) {
      const self = this;
      const stockId = self.$route.params.id;
      if (!stockId) return;
      self.api
        .addAdminitrativeCostPayment(stockId, payload)
        .then(() => {
          self.toast("Success", "Payment added", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onUpdateAdminCostPayment({ id, payload }) {
      const self = this;
      self.api
        .updateAdminitrativeCostPayment(id, payload)
        .then(() => {
          self.toast("Success", "Payment updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onRemoveAdminCostPayment(paymentId) {
      const self = this;
      self.api
        .removeAdminitrativeCostPayment(paymentId)
        .then(() => {
          self.toast("Success", "Payment removed", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    // ===== PurchasePayment handlers — single-bucket, multi-currency.
    // Each payment carries SupplierCurrency / ForeignAmount /
    // ExchangeRate / LocalAmount. Backend re-syncs the legacy
    // Purchase.VehiclePrice* columns after every CRUD.
    onAddPurchasePayment(payload) {
      const self = this;
      const stockId = self.$route.params.id;
      if (!stockId) return;
      self.api
        .addPurchasePayment(stockId, payload)
        .then(() => {
          self.toast("Success", "Payment added", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onUpdatePurchasePayment({ id, payload }) {
      const self = this;
      self.api
        .updatePurchasePayment(id, payload)
        .then(() => {
          self.toast("Success", "Payment updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onRemovePurchasePayment(paymentId) {
      const self = this;
      self.api
        .removePurchasePayment(paymentId)
        .then(() => {
          self.toast("Success", "Payment removed", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    // ===== SalePayment handlers — customer payments received against the
    //       Sale row. Single MYR (Expense-style, not Purchase-style).
    onAddSalePayment(payload) {
      const self = this;
      const stockId = self.$route.params.id;
      if (!stockId) return;
      self.api
        .addSalePayment(stockId, payload)
        .then(() => {
          self.toast("Success", "Payment added", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onUpdateSalePayment({ id, payload }) {
      const self = this;
      self.api
        .updateSalePayment(id, payload)
        .then(() => {
          self.toast("Success", "Payment updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onRemoveSalePayment(paymentId) {
      const self = this;
      self.api
        .removeSalePayment(paymentId)
        .then(() => {
          self.toast("Success", "Payment removed", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onEditAdminItem(item) {
      this.newAdminItem = item;
      this.addNewAdminItemPopupState = true;
    },
    onDeleteAdminItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        this.api
          .removeAdministrativeCostItem(this.itemAdminItemToDelete.id)
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
      self.itemAdminItemToDelete = {};
    },

    showDeleteAdminItemConfirmation(item) {
      var self = this;
      self.itemAdminItemToDelete = item;
      self.warningDeleteAdminItemModal = true;
    },

    onAddNewAdminItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        console.log(self.newAdminItem);

        if (self.newAdminItem.id) {
          this.api
            .updateAdministrativeCostItem(self.obj.id, self.newAdminItem)
            .then((response) => {
              self.toast("Success", "Admin Item Updated", "success");

              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        } else {
          console.log(self.newAdminItem);
          this.api
            .addAdministrativeCostItem(self.obj.id, self.newAdminItem)
            .then((response) => {
              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        }
      }
    },

    addAdminItemPopup() {
      this.resetNewAdminItem();
      this.addNewAdminItemPopupState = true;
    },
    resetNewAdminItem() {
      this.newAdminItem.id = null;
      this.newAdminItem.name = "";
      this.newAdminItem.amount = 0;
      this.newAdminItem.remarks = "";
    },

    //

    onEditArrivalItem(item) {
      this.newArrivalItem = item;
      this.addNewArrivalItemPopupState = true;
    },
    onDeleteArrivalItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        this.api
          .removeArrivalItem(this.itemArrivalItemToDelete.id)
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
      self.itemArrivalItemToDelete = {};
    },

    showDeleteArrivalItemConfirmation(item) {
      var self = this;
      self.itemArrivalItemToDelete = item;
      self.warningDeleteArrivalItemModal = true;
    },

    onAddNewArrivalItemConfirmation(status, evt, accept) {
      var self = this;
      if (accept) {
        console.log(self.newArrivalItem);

        if (self.newArrivalItem.id) {
          this.api
            .updateArrivalItem(self.obj.id, self.newArrivalItem)
            .then((response) => {
              self.toast("Success", "Arrival Item Updated", "success");

              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        } else {
          console.log(self.newArrivalItem);
          this.api
            .addArrivalItem(self.obj.id, self.newArrivalItem)
            .then((response) => {
              self.resetObj();
            })
            .catch(({ data }) => {
              self.toast("Error", helper.getErrorMessage(data), "danger");
            });
        }
      }
    },

    addArrivalItemPopup() {
      this.resetNewArrivalItem();
      this.addNewArrivalItemPopupState = true;
    },
    resetNewArrivalItem() {
      this.newArrivalItem.id = null;
      this.newArrivalItem.name = "";
      this.newArrivalItem.isAvailable = false;
      this.newArrivalItem.remarks = "";
    },

    onOpenDisburstmentLetter() {
      var self = this;
      if (self.obj && self.obj.id) {
        const newUrl = self.$router.resolve({
          path: `/admins/Disburstment/${self.obj.id}`,
        }).href;
        window.open(newUrl, "_blank"); // Opens in a new tab
      } else {
        console.error("Error: obj.id is undefined or invalid.");
      }
    },
    fetchArrivalStates() {
      var self = this;
      self.api
        .getArrivaleState()
        .then((response) => {
          var obj = response.result;
          this.arrivalStates = obj.map((state) => ({
            value: state.id,
            label: state.name,
          }));

          console.log(this.arrivalStates);
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    openLink(url) {
      window.open(url, "_blank");
    },
    maximizeImage(item) {
      this.currentImageUrl = this.getFullImageUrl(item); // Full-size image for the lightbox
      this.isMaximizeImageModal = true; // Show the modal
    },
    moveImageUp(index) {
      if (index > 0) {
        // Swap positions of images
        const currentImage = this.obj.vehicle.vehiclePhotoList[index];
        const previousImage = this.obj.vehicle.vehiclePhotoList[index - 1];

        currentImage.position--;
        previousImage.position++;

        this.sortAndSaveImages();
      }
    },
    moveImageDown(index) {
      if (index < this.obj.vehicle.vehiclePhotoList.length - 1) {
        // Swap positions of images
        const currentImage = this.obj.vehicle.vehiclePhotoList[index];
        const nextImage = this.obj.vehicle.vehiclePhotoList[index + 1];

        currentImage.position++;
        nextImage.position--;

        this.sortAndSaveImages();
      }
    },
    sortAndSaveImages() {
      // Sort images based on Position
      this.obj.vehicle.vehiclePhotoList.sort((a, b) => a.position - b.position);

      // Update the server with the new image positions if needed
      // this.updateImagePositionsOnServer();
      // console.log(this.obj.vehicle.vehiclePhotoList);
      var self = this;
      self.api
        .updateImagePosition(self.obj)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    //
    isDefaultImage(item) {
      return (
        "/Documents/File/" + item.documentId ===
        this.obj.vehicle.defaultImageUrl
      );
    },
    deleteImage(imageId) {
      if (confirm("Are you sure you want to delete this image?")) {
        var self = this;
        self.api
          .deleteVehicleImage(imageId)
          .then((response) => {
            self.toast("Message", "Updated", "success");
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });

        // Remove the image from the list
        // this.obj.vehicle.vehiclePhotoList =
        //   this.obj.vehicle.vehiclePhotoList.filter(
        //     (image) => image.id !== imageId
        //   );

        // // If the deleted image was the default, reset the default image URL
        // if (
        //   this.obj.vehicle.ImageDefaultUrl === this.getImageUrl({ id: imageId })
        // ) {
        //   this.obj.vehicle.ImageDefaultUrl = ""; // Reset or choose a new default
        // }

        // Add logic to delete the image on the server side
      }
    },
    setDefaultImage(item) {
      // Set the selected image's URL as the default
      // console.log(item);
      var self = this;
      self.obj.vehicle.defaultImageUrl = "/Documents/File/" + item.documentId;
      self.api
        .updateDefaultImages(self.obj)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });

      // Add logic to update the default image on the server side
    },
    getProfileFullName(item) {
      // console.log(item);
      try {
        return item.profile.fullName;
      } catch (error) {
        return "Unknown";
      }
    },
    addNewAdministrativeCostItem() {
      var self = this;
      self.api
        .addAdministrativeCostItem(self.obj.id, self.newAdministrativeCostItem)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeAdministrativeCostItem(item) {
      // console.log(item);
      var self = this;
      self.api
        .removeAdministrativeCostItem(item.id)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    //
    addNewExpenseItem() {
      var self = this;
      self.api
        .addExpenseItem(self.obj.id, self.newExpenseItem)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeExpensesItem(item) {
      // console.log(item);
      var self = this;
      self.api
        .removeExpenseItem(item.id)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    //
    addNewChecklistItem() {
      var self = this;
      self.api
        .addArrivalCheckLisItem(self.obj.id, self.newCheckListItem)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeArrivalChecklistItem(item) {
      // console.log(item);
      var self = this;
      self.api
        .removeArrivalCheckLisItem(item.id)
        .then((response) => {
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    isNewApplication() {
      if (this.$route.params.id) return false;
      return true;
    },
    getCollectionDocumentName(item) {
      if (item.document == null) return "Unknown";
      if (item.document.fileName == null) return "Unknown";

      return item.document.fileName;
    },
    getCollectionDocumentUrl(item) {
      var self = this;
      return apiUrl + "documents/file/" + item.documentId;
    },

    removeLouDocumentConfirmation(item) {
      var self = this;
      self.louDocumentToDelete = item;
      self.removeLouDocumentWarningModal = true;
    },
    onRemoveLouDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeLouDocuments(
            self.louDocumentToDelete.loanId,
            self.louDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    removeReceiptDocumentConfirmation(item) {
      var self = this;
      self.receiptDocumentToDelete = item;
      self.removeReceiptDocumentWarningModal = true;
    },
    onRemoveReceiptDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeReceiptDocuments(
            self.receiptDocumentToDelete.saleId,
            self.receiptDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    removeK8DocumentConfirmation(item) {
      var self = this;
      self.k8DocumentToDelete = item;
      self.removeK8DocumentWarningModal = true;
    },
    onRemoveK8DocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeK8Documents(
            self.k8DocumentToDelete.clearanceId,
            self.k8DocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    removeK1DocumentConfirmation(item) {
      var self = this;
      self.k1DocumentToDelete = item;
      self.removeK1DocumentWarningModal = true;
    },
    onRemoveK1DocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeK1Documents(
            self.k1DocumentToDelete.clearanceId,
            self.k1DocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    removePuspakomB7SlipDocumentConfirmation(item) {
      var self = this;
      self.puspakomB7SlipDocumentToDelete = item;
      self.removePuspakomB7SlipDocumentWarningModal = true;
    },
    onChangeStockNoConfirmation(show) {
    // This will be called when the modal show state changes
    if (!show) {
      this.resetObj();
      // this.resetForm();
    }
  },
  
  cancelChangeStockNo() {
    // Close the modal without saving
    this.changeStockNoPopup = false;
    this.resetObj();
  },
  
  async confirmChangeStockNo() {
    // Validate the input
    if (!this.newStockNo || this.newStockNo.trim() === '') {
      this.self.toast("Error", 'Please enter a valid stock number', "danger");
      return;
    }

    var data = {
      newStockNo: this.newStockNo
    }

    this.api
          .changeStockNo(this.obj.id, data)
          .then((response) => {
            this.resetObj();
            this.changeStockNoPopup = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
          });

    
    // try {
    //   // Your API call to update stock number
    //   await this.updateStockNumber();
      
    //   // Only close the modal if the update was successful
    //   this.changeStockNoPopup = false;
    //   this.resetForm();
    //   this.$toast.success('Stock number updated successfully');
      
    // } catch (error) {
    //   // Handle error - modal stays open
    //   this.$toast.error(error.message || 'Failed to update stock number');
    //   // Modal remains open so user can try again
    // }
  },
  
  // resetForm() {
  //   this.newStockNo = '';
  // },
  
  // async updateStockNumber() {
  //   // Your API call logic here
  //   const response = await this.$http.put(`/api/stocks/UpdateStockNo/${this.stockId}`, {
  //     newStockNo: this.newStockNo
  //   });
    
  //   if (!response.data.success) {
  //     throw new Error(response.data.message);
  //   }
    
  //   return response.data;
  // },

    // onChangeStockNoConfirmation(status, evt, accept) {
    //   if (accept) {
    //     var self = this;
    //     self.api
    //       .changeStockNo(self.obj.id, self.newStockNo)
    //       .then((response) => {
    //         self.resetObj();
    //         self.changeStockNoPopup = false;
    //       })
    //       .catch(({ data }) => {
    //         self.toast("Error", helper.getErrorMessage(data), "danger");
    //       });
    //   }
    // },
    onRemovePuspakomB7SlipDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removePuspakomB7SlipDocuments(
            self.puspakomB7SlipDocumentToDelete.registrationId,
            self.puspakomB7SlipDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },
    removePuspakomB2SlipDocumentConfirmation(item) {
      var self = this;
      self.puspakomB2SlipDocumentToDelete = item;
      self.removePuspakomB2SlipDocumentWarningModal = true;
    },
    onRemovePuspakomB2SlipDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removePuspakomB2SlipDocuments(
            self.puspakomB2SlipDocumentToDelete.registrationId,
            self.puspakomB2SlipDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },
    removeJpjDaftarDocumentConfirmation(item) {
      var self = this;
      self.jpjDaftarDocumentToDelete = item;
      self.removeJpjDaftarDocumentWarningModal = true;
    },
    onRemoveJpjDaftarDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeJpjDaftarDocuments(
            self.jpjDaftarDocumentToDelete.registrationId,
            self.jpjDaftarDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    removeJpjHakMilikDocumentConfirmation(item) {
      var self = this;
      self.jpjHakMilikDocumentToDelete = item;
      self.removeJpjHakMilikDocumentWarningModal = true;
    },
    onRemoveJpjHakMilikDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeJpjHakMilikDocuments(
            self.jpjHakMilikDocumentToDelete.registrationId,
            self.jpjHakMilikDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },
    //
    removeJpjGeranDocumentConfirmation(item) {
      var self = this;
      self.jpjGeranDocumentToDelete = item;
      self.removeJpjGeranDocumentWarningModal = true;
    },
    onRemoveJpjGeranDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeJpjGeranDocuments(
            self.jpjGeranDocumentToDelete.registrationId,
            self.jpjGeranDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },
    //
    removeBolDocumentConfirmation(item) {
      var self = this;
      self.bolDocumentToDelete = item;
      self.removeBolDocumentWarningModal = true;
    },
    onRemoveBolDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeBolDocuments(
            self.bolDocumentToDelete.importId,
            self.bolDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // Insurance Document handlers
    insurance_uploaded(data) {
      var self = this;
      self.api
        .updateInsuranceDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeInsuranceDocumentConfirmation(item) {
      var self = this;
      self.insuranceDocumentToDelete = item;
      self.removeInsuranceDocumentWarningModal = true;
    },
    onRemoveInsuranceDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeInsuranceDocuments(
            self.insuranceDocumentToDelete.registrationId,
            self.insuranceDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // RoadTax Document handlers
    roadTax_uploaded(data) {
      var self = this;
      self.api
        .updateRoadTaxDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeRoadTaxDocumentConfirmation(item) {
      var self = this;
      self.roadTaxDocumentToDelete = item;
      self.removeRoadTaxDocumentWarningModal = true;
    },
    onRemoveRoadTaxDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeRoadTaxDocuments(
            self.roadTaxDocumentToDelete.registrationId,
            self.roadTaxDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // ReceiptEDaftar Document handlers
    receiptEDaftar_uploaded(data) {
      var self = this;
      self.api
        .updateReceiptEDaftarDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeReceiptEDaftarDocumentConfirmation(item) {
      var self = this;
      self.receiptEDaftarDocumentToDelete = item;
      self.removeReceiptEDaftarDocumentWarningModal = true;
    },
    onRemoveReceiptEDaftarDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeReceiptEDaftarDocuments(
            self.receiptEDaftarDocumentToDelete.registrationId,
            self.receiptEDaftarDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // ReceiptKastam Document handlers
    receiptKastam_uploaded(data) {
      var self = this;
      self.api
        .updateReceiptKastamDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeReceiptKastamDocumentConfirmation(item) {
      var self = this;
      self.receiptKastamDocumentToDelete = item;
      self.removeReceiptKastamDocumentWarningModal = true;
    },
    onRemoveReceiptKastamDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeReceiptKastamDocuments(
            self.receiptKastamDocumentToDelete.registrationId,
            self.receiptKastamDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // ExportCertificate Document handlers
    exportCertificate_uploaded(data) {
      var self = this;
      self.api
        .updateExportCertificateDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeExportCertificateDocumentConfirmation(item) {
      var self = this;
      self.exportCertificateDocumentToDelete = item;
      self.removeExportCertificateDocumentWarningModal = true;
    },
    onRemoveExportCertificateDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeExportCertificateDocuments(
            self.exportCertificateDocumentToDelete.clearanceId,
            self.exportCertificateDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    // CustomerIc Document handlers
    customerIc_uploaded(data) {
      var self = this;
      self.api
        .updateCustomerIcDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    removeCustomerIcDocumentConfirmation(item) {
      var self = this;
      self.customerIcDocumentToDelete = item;
      self.removeCustomerIcDocumentWarningModal = true;
    },
    onRemoveCustomerIcDocumentConfirmation(status, evt, accept) {
      if (accept) {
        var self = this;
        self.api
          .removeCustomerIcDocuments(
            self.customerIcDocumentToDelete.saleId,
            self.customerIcDocumentToDelete.documentId
          )
          .then((response) => {
            self.resetObj();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },

    setBookingStartDate(e) {
      this.bookingStartDate = e.target.value;
    },
    onBookingToggle(checked) {
      this.obj.isBooked = checked;
      if (checked) {
        // Starting a fresh booking — default From to today, Expiry to +10 days.
        this.bookingStartDate = moment().format("YYYY-MM-DD");
        this.bookingEndDate = moment().add(10, "days").format("YYYY-MM-DD");
      }
    },
    onLouToggle(checked) {
      this.obj.isLou = checked;
      if (checked) {
        // Starting a fresh LOU — default From to today, Expiry to +5 days
        // (mirrors setLouStartDate's auto-calc convention).
        this.louStartDate = moment().format("YYYY-MM-DD");
        this.louEndDate = moment().add(5, "days").format("YYYY-MM-DD");
      }
    },
    setBookingEndDate(e) {
      this.bookingEndDate = e.target.value;
    },
    setLouStartDate(e) {
      this.louStartDate = e.target.value;
      // Auto-calculate expiry date (5 days later)
      const startDate = moment(e.target.value);
      const expiryDate = startDate.add(5, 'days');
      this.louEndDate = expiryDate.format('YYYY-MM-DD');
    },
    setLouEndDate(e) {
      this.louEndDate = e.target.value;
    },

    setMudahStartDate(e) {
      this.mudahStartDate = e.target.value;
    },
    setMudahEndDate(e) {
      this.mudahEndDate = e.target.value;
    },

    setCarListStartDate(e) {
      this.carListStartDate = e.target.value;
    },
    setCarListEndDate(e) {
      this.carListEndDate = e.target.value;
    },

    setCariCarzStartDate(e) {
      this.cariCarzStartDate = e.target.value;
    },
    setCariCarzEndDate(e) {
      this.cariCarzEndDate = e.target.value;
    },

    setEstimateDateOfDeparture(e) {
      this.estimateDateOfDeparture = new Date(e.target.value).getTime();
    },
    setEstimateDateOfArrival(e) {
      this.estimateDateOfArrival = e.target.value;
    },
    setDateOfBillOfLading(e) {
      this.dateOfBillOfLading = e.target.value;
      // Auto-calculate ETA (14 days later)
      if (e.target.value) {
        const bolDate = moment(e.target.value);
        const etaDate = bolDate.add(14, 'days');
        this.estimateDateOfArrival = etaDate.format('YYYY-MM-DD');
      }
    },

    setVehicleRegistrationDate(e) {
      this.vehicleRegistrationDate = e.target.value;
    },

    setSystemRegistrationDate(e) {
      this.systemRegistrationDate = new Date(e.target.value).getTime();
    },

    onImageClick(item) {},
    getImageUrl(item) {
      // Thumbnail for the photo grid. Server-side resize to 256px keeps the
      // photo wall loading in under a second instead of ~45s for the full bytea.
      var url = apiUrl + "documents/Thumbnail/" + item.documentId + "?size=256";
      return url;
    },
    getFullImageUrl(item) {
      // Full-size image for the click-to-enlarge modal.
      return apiUrl + "Documents/File/" + item.documentId;
    },
    puspakomB7Slip_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updatePuspakomB7SlipDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    puspakomB2Slip_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updatePuspakomB2SlipDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          // self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    jpjDaftar_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateJpjDaftarDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //  self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    jpjHakMilik_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateJpjHakMilikDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //   self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    jpjGeran_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateJpjGeranDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //   self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    bol_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateBolDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          // self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    k8_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateK8Documents(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //    self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    k1_uploaded(data) {
      // console.log(data);
      var self = this;
      self.api
        .updateK1Documents(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //    self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    lou_uploaded(data) {
      var self = this;
      self.api
        .updateLouDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
          //   self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    receipt_uploaded(data) {
      var self = this;
      self.api
        .updateReceiptDocuments(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.refreshDocumentList();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    media_uploaded(data) {
      var self = this;
      self.api
        .updateVehicleImages(self.obj.id, data.uploadedFiles)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //
    onVehicleTypeSelect() {
      this.obj.vehicle.vehicleTypeId = this.selectedVehicleTypeId;
      // console.log(this.obj.vehicle.vehicleTypeId);
    },
    //Model Start
    onSearchModel() {
      var self = this;
      if (!self.selectedBrand || !self.selectedBrand.id) {
        self.toast("Pick a Brand first", "Please select a Brand before searching for a Model.", "warning");
        return;
      }
      self.refreshTableModel();
      self.modelSearchPopup = true;
    },
    onModelSelected(item) {
      var self = this;
      self.selectedModel = item;
      self.obj.vehicle.modelId = item.id.toString();

      self.modelSearchPopup = false;
    },
    refreshTableModel() {
      var self = this;
      self.loading = true;
      self.modelApi
        .getListByBrandId(self.selectedBrand.id)
        .then((response) => {
          self.modelList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Model End

    //Brand Start
    onSearchBrand() {
      var self = this;
      self.refreshTableBrand();
      self.brandSearchPopup = true;
    },
    onBrandSelected(item) {
      var self = this;
      self.selectedBrand = item;
      self.obj.vehicle.brandId = item.id.toString();

      self.brandSearchPopup = false;
    },
    refreshTableBrand() {
      var self = this;
      self.loading = true;
      self.brandApi
        .getList()
        .then((response) => {
          self.brandList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Brand End

    //AP Company
    onSearchApSubCompany() {
      var self = this;
      self.refreshTableSubCompany();
      self.subCompanySearchPopup = true;
    },
    onSubCompanySelected(item) {
      var self = this;
      self.selectedApSubCompany = item;
      if (self.obj.apCompany.subCompanyId != item.id.toString()) {
        self.obj.apCompany.subCompanyId = item.id.toString();
        self.obj.apCompany.bankAccountId = "";
        self.obj.apCompany.bankAccount = {};
        self.selectedApBankAccount = {};
      }
      self.subCompanySearchPopup = false;
    },
    refreshTableSubCompany() {
      var self = this;
      self.loading = true;
      self.subCompanyApi
        .getList()
        .then((response) => {
          self.subCompanyList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //AP Company

    //Bank Account Start
    onSearchApBankAccount() {
      var self = this;
      self.refreshTableBankAccount();
      self.bankAccountSearchPopup = true;
    },
    onBankAccountSelected(item) {
      var self = this;
      self.selectedApBankAccount = item;
      self.obj.apCompany.bankAccountId = item.id.toString();

      self.bankAccountSearchPopup = false;
    },
    onOpenBankAccountAddModal() {
      var self = this;
      // Initialize form with SubCompany ID
      self.bankAccountForm = {
        name: "",
        address: "",
        accountName: "",
        accountNo: "",
        accountType: "",
        subCompanyId: self.selectedApSubCompany.id,
      };
      self.bankAccountFormModal = true;
    },
    onCancelBankAccountForm() {
      var self = this;
      self.bankAccountFormModal = false;
      self.bankAccountForm = {
        name: "",
        address: "",
        accountName: "",
        accountNo: "",
        accountType: "",
        subCompanyId: null,
      };
    },
    onSaveBankAccount() {
      var self = this;

      // Validation
      if (!self.bankAccountForm.name || self.bankAccountForm.name.trim() === "") {
        self.toast("Error", "Bank account name is required", "danger");
        return;
      }

      self.loading = true;
      self.bankAccountApi
        .create(self.bankAccountForm)
        .then((response) => {
          self.toast("Success", "Bank account created successfully", "success");
          self.bankAccountFormModal = false;

          // Refresh the bank account list
          self.refreshTableBankAccount();

          // Auto-select the newly created bank account
          if (response.result) {
            self.selectedApBankAccount = response.result;
            self.obj.apCompany.bankAccountId = response.result.id.toString();
          }

          // Reset form
          self.bankAccountForm = {
            name: "",
            address: "",
            accountName: "",
            accountNo: "",
            accountType: "",
            subCompanyId: null,
          };
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        })
        .finally(() => {
          self.loading = false;
        });
    },
    refreshTableBankAccount() {
      var self = this;
      self.loading = true;
      self.bankAccountApi
        .getListBySubCompanyId(self.selectedApSubCompany.id)
        .then((response) => {
          self.bankAccountList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Bank Account End

    //Supplier Start
    onSearchSupplier() {
      var self = this;
      self.refreshTableSupplier();
      self.supplierSearchPopup = true;
    },
    onSupplierSelected(item) {
      var self = this;
      self.selectedSupplier = item;
      self.obj.purchase.supplierId = item.id.toString();

      self.supplierSearchPopup = false;
    },
    refreshTableSupplier() {
      var self = this;
      self.loading = true;
      self.supplierApi
        .getList()
        .then((response) => {
          self.supplierList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Supplier End

    //Customer Start
    onSearchCustomer() {
      // Reset filters and pagination when opening modal
      this.customerColumnFilterValue = {};
      this.customerActivePage = 1;
      
      this.refreshTableCustomer();
      this.customerSearchPopup = true;
    },
    onSearchSalesMan() {
      var self = this;
      self.refreshTableSalesman();
      self.salesmanSearchPopup = true;
    },
    onChangeStockNo() {
      this.newStockNo = this.obj.stockNo;
      this.changeStockNoPopup = true;
    },
    onSearchShowroom() {
      var self = this;
      self.refreshTableShowroom();
      self.showroomSearchPopup = true;
    },
    onCustomerSelected(item) {
      this.selectedCustomer = item;
      this.obj.sale.customerId = item.id.toString();
      this.customerSearchPopup = false;
      
      // Show success message
      this.toast("Success", `Customer "${item.name}" selected`, "success");
    },

    // Removed onCustomerSearchInput method - using column filters only

    onCreateNewCustomer() {
      // Open nested modal instead of navigating
      this.customerCreatePopup = true;
      // Don't close search modal - keep it in background
    },
    onCancelCustomerCreation() {
      this.customerCreatePopup = false;
      this.resetNewCustomerForm();
    },
    resetNewCustomerForm() {
      this.newCustomer = {
        customerType: "",
        name: "",
        icNumber: "",
        tinNumber: "",
        address: "",
        email: "",
        phone: "",
      };
    },
    onSaveNewCustomer() {
      const self = this;

      // Validation
      if (!self.newCustomer.name || self.newCustomer.name.length < 2) {
        self.toast("Validation Error", "Customer name is required (minimum 2 characters)", "danger");
        return;
      }

      self.creatingCustomer = true;

      // Call API to create customer
      self.customerApi
        .create(self.newCustomer)
        .then((response) => {
          const createdCustomer = response.result;

          // Auto-select the newly created customer
          self.selectedCustomer = createdCustomer;
          self.obj.sale.customerId = createdCustomer.id.toString();

          // Close both modals
          self.customerCreatePopup = false;
          self.customerSearchPopup = false;

          // Reset form
          self.resetNewCustomerForm();

          // Show success message
          self.toast("Success", `Customer "${createdCustomer.name}" created and selected`, "success");

          self.creatingCustomer = false;
        })
        .catch(({ data }) => {
          self.toast("Error", self.helper.getErrorMessage(data), "danger");
          self.creatingCustomer = false;
        });
    },
    onSalesmanSelected(item) {
      var self = this;
      self.selectedSalesman = item;
      self.obj.sale.salesManId = item.id.toString();
      self.obj.sale.salesmanId = item.id.toString();

      self.salesmanSearchPopup = false;
    },
    onShowroomSelected(item) {
      var self = this;
      self.selectedShowroom = item;
      self.obj.showRoomId = item.id.toString();
      self.showroomSearchPopup = false;
    },
    refreshTableCustomer() {
      this.customerLoading = true;
      
      const params = {
        page: this.customerActivePage,
        itemsPerPage: this.customerItemsPerPage,
        sorter: this.customerSorterValue,
        filters: this.customerColumnFilterValue,
        search: "", // Removed general search, using column filters only
      };

      console.log("Loading customers with params:", params);

      this.customerApi
        .getList(params)
        .then((response) => {
          if (response.result) {
            this.customerList = response.result.items;
            this.customerPages = Math.ceil(response.result.totalItems / this.customerItemsPerPage);
            console.log("Loaded customers:", this.customerList.length);
          } else {
            this.customerList = [];
            this.customerPages = 1;
          }
          this.customerLoading = false;
        })
        .catch((error) => {
          console.error("Error loading customers:", error);
          this.toast("Error", "Failed to load customers", "danger");
          this.customerList = [];
          this.customerLoading = false;
        });
    },
    refreshTableSalesman() {
      var self = this;
      self.loading = true;
      self.profileApi
        .getSalesmanCandidates()
        .then((response) => {
          self.salesmanList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    refreshTableShowroom() {
      var self = this;
      self.loading = true;
      self.showroomApi
        .getList()
        .then((response) => {
          self.showroomList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Customer End

    //Bank Start
    onSearchBank() {
      var self = this;
      self.refreshTableBank();
      self.bankSearchPopup = true;
    },
    onBankSelected(item) {
      var self = this;
      self.selectedBank = item;
      self.obj.sale.loan.bankId = item.id.toString();

      self.bankSearchPopup = false;
    },
    onCreateNewBank() {
      var self = this;
      self.bankCreatePopup = true;
      // Don't close bank search modal - keeps it in background
    },
    onSaveNewBank() {
      var self = this;

      // Validation
      if (!self.newBank.name || self.newBank.name.length < 2) {
        self.toast("Validation Error", "Bank name is required (minimum 2 characters)", "danger");
        return;
      }

      self.creatingBank = true;

      // Call API to create bank
      self.bankApi.create(self.newBank)
        .then((response) => {
          const createdBank = response.result;

          // Auto-select the newly created bank
          self.selectedBank = createdBank;
          self.obj.sale.loan.bankId = createdBank.id.toString();

          // Close both modals
          self.bankCreatePopup = false;
          self.bankSearchPopup = false;

          // Reset form
          self.resetNewBankForm();

          // Show success message
          self.toast("Success", `Bank "${createdBank.name}" created and selected`, "success");

          self.creatingBank = false;
        })
        .catch(({ data }) => {
          self.toast("Error", self.helper.getErrorMessage(data), "danger");
          self.creatingBank = false;
        });
    },
    onCancelBankCreation() {
      var self = this;
      self.bankCreatePopup = false;
      self.resetNewBankForm();
    },
    resetNewBankForm() {
      var self = this;
      self.newBank = {
        name: "",
        address: "",
        phone: "",
        email: "",
        website: "",
      };
    },
    refreshTableBank() {
      var self = this;
      self.loading = true;
      self.bankApi
        .getList()
        .then((response) => {
          self.bankList = response.result;
          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    //Bank End
    getDisplayDateTime(dt) {
      return formatMalaysiaDateTime(dt);
    },
    submitNewMessage() {
      var self = this;
      if (self.remark == "") return;
      var remarks = {
        stockId: self.obj.id,
        remark: self.remark,
      };
      self.api
        .updateRemarks(remarks)
        .then((response) => {
          self.resetObj();
          self.remark = "";
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    updateStockStatus() {
      var self = this;
      var stockStatusInfo = {
        stockId: self.obj.id,
        stockStatusId: self.selectedStockStatusId,
      };
      self.api
        .updateStockStatus(stockStatusInfo)
        .then((response) => {
          self.toast("Message", "Updated", "success");
          self.resetObj();
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    refreshStockStatus() {
      var self = this;
      self.loading = false;
      self.stockStatusApi
        .getList()
        .then((response) => {
          for (let i = 0; i < response.result.length; i++) {
            self.stockStatuses.push({
              value: response.result[i].id,
              label: response.result[i].name,
            });

            if (response.result[i].name == "Draft") {
              self.selectedStockStatusId = response.result[i].id;
            }
          }

          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },
    refreshVehicleType() {
      var self = this;
      self.loading = false;
      self.vehicleTypeApi
        .getList()
        .then((response) => {
          for (let i = 0; i < response.result.length; i++) {
            self.vehicleTypes.push({
              value: response.result[i].id,
              label: response.result[i].name,
            });
          }

          self.loading = false;
        })
        .catch(({ data }) => {
          self.toast("Error", helper.getErrorMessage(data), "danger");
        });
    },

    onSubmit() {
      var self = this;
      // self.obj.packageCategories = self.categoryList;
      // for (let i = 0; i < self.obj.packageCategories.length; i++) {
      //   self.obj.packageCategories[i].brand = null;
      // }
      console.log("self.selectedArrivalState", self.selectedArrivalState);
      if (self.selectedArrivalState)
        self.obj.arrivalState = self.selectedArrivalState;

      if (self.selectedShowroom) {
        self.obj.showRoomId = self.selectedShowroom.id;
      }

      if (self.selectedSalesman) {
        self.obj.sale.salesmanId = self.selectedSalesman.id;
      }

      if (self.selectedCustomer) {
        self.obj.sale.customerId = self.selectedCustomer.id;
      }

      self.obj.sale.bookingDate = moment(self.bookingStartDate).format(
        "YYYY-MM-DD"
      );
      self.obj.sale.bookingExpiryDate = moment(self.bookingEndDate).format(
        "YYYY-MM-DD"
      );

      self.obj.sale.louDate = moment(self.louStartDate).format(
        "YYYY-MM-DD"
      );
      self.obj.sale.louExpiryDate = moment(self.louEndDate).format(
        "YYYY-MM-DD"
      );

      self.obj.advertisement.mudahStartDate = moment(
        self.mudahStartDate
      ).format("YYYY-MM-DD");
      self.obj.advertisement.mudahEndDate = moment(self.mudahEndDate).format(
        "YYYY-MM-DD"
      );

      self.obj.advertisement.carListStartDate = moment(
        self.carListStartDate
      ).format("YYYY-MM-DD");
      self.obj.advertisement.carListEndDate = moment(
        self.carListEndDate
      ).format("YYYY-MM-DD");

      self.obj.advertisement.cariCarzStartDate = moment(
        self.cariCarzStartDate
      ).format("YYYY-MM-DD");
      self.obj.advertisement.cariCarzEndDate = moment(
        self.cariCarzEndDate
      ).format("YYYY-MM-DD");

      self.obj.import.estimateDateOfDeparture = moment(
        self.estimateDateOfDeparture
      ).format();

      self.obj.import.estimateDateOfArrival = moment(
        self.estimateDateOfArrival
      ).format("YYYY-MM-DD");

      self.obj.import.dateOfBillOfLading = moment(
        self.dateOfBillOfLading
      ).format("YYYY-MM-DD");

      self.obj.registration.vehicleRegistrationDate = moment(
        self.vehicleRegistrationDate
      ).format("YYYY-MM-DD");

      self.obj.registrationDate = moment(self.systemRegistrationDate).format();

      if (!self.obj.id) {
        console.log("onSubmit self.obj", self.obj);
        this.api
          .create(self.obj)
          .then((response) => {
            // console.log(response);
            self.obj = response.result;
            self.$router.push({ path: `/admins/Stock/${response.result.id}` });
            self.reset();
            //self.$router.push({ path: "/admins/StockList" });
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            // self.$router.push({ path: `/admins/Stock/${self.obj.id}` });
            self.toast("Info", "Update success", "success");
            // this.$router.go();
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      }
    },
    onReset(evt) {
      evt.preventDefault();
      this.obj = {};
    },
    toast(header, message, color) {
      var self = this;
      const toastIndex = self.infoList.length;
      self.infoList.push({
        header: header,
        message: message,
        color: color,
      });

      // Remove toast from array after autohide duration (3000ms + 500ms buffer)
      setTimeout(() => {
        if (self.infoList[toastIndex]) {
          self.infoList.splice(toastIndex, 1);
        }
      }, 3500);
    },
    refreshDocumentList() {
      var self = this;
      try {
        self.api
          .get(self.obj.id)
          .then((response) => {
            console.log(response.result);
            self.obj.import.billOfLandingDocuments =
              response.result.import.billOfLandingDocuments;
            self.obj.clearance.k8Documents =
              response.result.clearance.k8Documents;
            self.obj.sale.loan.letterOfUndertakingDocuments =
              response.result.sale.loan.letterOfUndertakingDocuments;

            self.obj.registration.jpjEHakMilikDocuments =
              response.result.registration.jpjEHakMilikDocuments;
            self.obj.registration.puspakomB7SlipDocuments =
              response.result.registration.puspakomB7SlipDocuments;
            self.obj.registration.puspakomB2SlipDocuments =
              response.result.registration.puspakomB2SlipDocuments;
            self.obj.registration.jpjEDaftarDocuments =
              response.result.registration.jpjEDaftarDocuments;

            self.jpjHakMilikDocumentList =
              response.result.registration.jpjEHakMilikDocuments;
            self.jpjGeranDocumentList =
              response.result.registration.jpjGeranDocuments;

            self.puspakomB7SlipDocumentList =
              response.result.registration.puspakomB7SlipDocuments;
            self.puspakomB2SlipDocumentList =
              response.result.registration.puspakomB2SlipDocuments;
            self.jpjDaftarDocumentList =
              response.result.registration.jpjEDaftarDocuments;

            self.bolDocumentList =
              response.result.import.billOfLandingDocuments;
            self.k8DocumentList = response.result.clearance.k8Documents;
            self.k1DocumentList = response.result.clearance.k1Documents;
            // console.log("self.obj.clearance.k1Documents",response.result.clearance.k1Documents);
            // console.log("self.k1DocumentList",self.k1DocumentList);
            // console.log("self.k8DocumentList",self.k8DocumentList);

            self.louDocumentList =
              response.result.sale.loan.letterOfUndertakingDocuments;
            self.receiptDocumentList = response.result.sale.receiptDocuments || [];

            // New document lists
            self.insuranceDocumentList = response.result.registration.insuranceDocuments || [];
            self.roadTaxDocumentList = response.result.registration.roadTaxDocuments || [];
            self.receiptEDaftarDocumentList = response.result.registration.receiptEDaftarDocuments || [];
            self.receiptKastamDocumentList = response.result.registration.receiptKastamDocuments || [];
            self.exportCertificateDocumentList = response.result.clearance.exportCertificateDocuments || [];
            self.customerIcDocumentList = response.result.sale.customerIcDocuments || [];
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          });
      } catch (error) {}
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        this.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            console.log("self.obj", self.obj);

            // Initialize null objects to prevent template errors
            if (!self.obj.advertisement) {
              self.obj.advertisement = {};
            }
            if (!self.obj.sale) {
              self.obj.sale = { loan: {}, customer: null, salesMan: null };
            }
            if (!self.obj.purchase) {
              self.obj.purchase = { supplier: null };
            }
            if (!self.obj.import) {
              self.obj.import = {};
            }
            if (!self.obj.expense) {
              self.obj.expense = {};
            }

            self.selectedArrivalState = self.obj.arrivalState;

            self.selectedStockStatusId =
              self.obj.latestStockStatus?.stockStatusId || null;
            // console.log(self.obj.vehicle.vehicleTypeId);
            if (self.obj.vehicle.vehicleTypeId != null)
              self.selectedVehicleTypeId = self.obj.vehicle.vehicleTypeId;

            if (self.obj.apCompany.subCompany != null)
              self.selectedApSubCompany = self.obj.apCompany.subCompany;

            if (self.obj.apCompany.bankAccount != null)
              self.selectedApBankAccount = self.obj.apCompany.bankAccount;

            if (self.obj.vehicle.brand != null)
              self.selectedBrand = self.obj.vehicle.brand;

            // if (self.obj.showroom.lotNo != null)
            //   self.selectedShowroom = self.obj.sale.lotNo

            if (self.obj.showRoom != null)
              self.selectedShowroom = self.obj.showRoom;

            // console.log("self.selectedShowroom", self.selectedShowroom);
            if (self.obj.vehicle.model != null)
              self.selectedModel = self.obj.vehicle.model;
            if (self.obj.purchase.supplier != null)
              self.selectedSupplier = self.obj.purchase.supplier;

            if (self.obj.sale.customer != null)
              self.selectedCustomer = self.obj.sale.customer;

            if (self.obj.sale.salesMan != null)
              self.selectedSalesman = self.obj.sale.salesMan;

            if (self.obj.sale.loan.bank != null)
              self.selectedBank = self.obj.sale.loan.bank;

            self.bookingStartDate = self.obj.sale.bookingDate
              ? moment(self.obj.sale.bookingDate).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD");

            self.bookingEndDate = self.obj.sale.bookingExpiryDate
              ? moment(self.obj.sale.bookingExpiryDate).format("YYYY-MM-DD")
              : "";

            self.louStartDate = self.obj.sale.louDate
              ? moment(self.obj.sale.louDate).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD");

            self.louEndDate = self.obj.sale.louExpiryDate
              ? moment(self.obj.sale.louExpiryDate).format("YYYY-MM-DD")
              : "";

            self.mudahStartDate = self.obj.advertisement?.mudahStartDate
              ? moment(self.obj.advertisement.mudahStartDate).format(
                  "YYYY-MM-DD"
                )
              : "2001-01-01";

            self.mudahEndDate = self.obj.advertisement?.mudahEndDate
              ? moment(self.obj.advertisement.mudahEndDate).format("YYYY-MM-DD")
              : "2001-01-01";

            self.carListStartDate = self.obj.advertisement?.carListStartDate
              ? moment(self.obj.advertisement.carListStartDate).format(
                  "YYYY-MM-DD"
                )
              : "2001-01-01";

            self.carListEndDate = self.obj.advertisement?.carListEndDate
              ? moment(self.obj.advertisement.carListEndDate).format(
                  "YYYY-MM-DD"
                )
              : "2001-01-01";

            self.cariCarzStartDate = self.obj.advertisement?.cariCarzStartDate
              ? moment(self.obj.advertisement.cariCarzStartDate).format(
                  "YYYY-MM-DD"
                )
              : "2001-01-01";

            self.cariCarzEndDate = self.obj.advertisement?.cariCarzEndDate
              ? moment(self.obj.advertisement.cariCarzEndDate).format(
                  "YYYY-MM-DD"
                )
              : "2001-01-01";

            self.estimateDateOfDeparture =
              self.obj.import?.estimateDateOfDeparture || null;
            self.estimateDateOfArrival = self.obj.import?.estimateDateOfArrival
              ? moment(self.obj.import.estimateDateOfArrival).format("YYYY-MM-DD")
              : "";

            self.dateOfBillOfLading = self.obj.import?.dateOfBillOfLading
              ? moment(self.obj.import.dateOfBillOfLading).format("YYYY-MM-DD")
              : "";

            self.jpjHakMilikDocumentList =
              self.obj.registration?.jpjEHakMilikDocuments || [];

            self.jpjGeranDocumentList = self.obj.registration?.jpjGeranDocuments || [];

            self.puspakomB7SlipDocumentList =
              self.obj.registration?.puspakomB7SlipDocuments || [];
            self.puspakomB2SlipDocumentList =
              self.obj.registration?.puspakomB2SlipDocuments || [];
            self.jpjDaftarDocumentList =
              self.obj.registration?.jpjEDaftarDocuments || [];
            console.log("1");

            self.bolDocumentList = self.obj.import?.billOfLandingDocuments || [];
            self.k8DocumentList = self.obj.clearance?.k8Documents || [];
            self.k1DocumentList = self.obj.clearance?.k1Documents || [];
            console.log("self.k8DocumentList", self.k8DocumentList);

            self.louDocumentList =
              self.obj.sale?.loan?.letterOfUndertakingDocuments || [];
            self.receiptDocumentList = self.obj.sale?.receiptDocuments || [];

            // New document lists
            self.insuranceDocumentList = self.obj.registration?.insuranceDocuments || [];
            self.roadTaxDocumentList = self.obj.registration?.roadTaxDocuments || [];
            self.receiptEDaftarDocumentList = self.obj.registration?.receiptEDaftarDocuments || [];
            self.receiptKastamDocumentList = self.obj.registration?.receiptKastamDocuments || [];
            self.exportCertificateDocumentList = self.obj.clearance?.exportCertificateDocuments || [];
            self.customerIcDocumentList = self.obj.sale?.customerIcDocuments || [];

            self.administrativeCostList =
              self.obj.adminitrativeCost?.adminitrativeCostItems || [];
            self.expensesList = self.obj.expense?.expenses || [];
            self.arrivalChecklistList =
              self.obj.arrivalChecklist?.arrivalChecklists || [];

            self.vehicleRegistrationDate =
              self.obj.registration?.vehicleRegistrationDate
                ? moment(self.obj.registration.vehicleRegistrationDate).format("YYYY-MM-DD")
                : "";

            self.systemRegistrationDate = self.obj.registrationDate;

            if (self.obj.vehicle.vehiclePhotoList.length > 0) {
              if (self.obj.vehicle.vehiclePhotoList[0].position == 0) {
                for (
                  var i = 0;
                  i < self.obj.vehicle.vehiclePhotoList.length;
                  i++
                ) {
                  self.obj.vehicle.vehiclePhotoList[i].position = i + 1;
                }
              }
            }

            // console.log(self.obj.vehicle.vehiclePhotoList);

            // self.updatLatestStockStatus(self.obj.latestStockStatus);
            // if (self.obj.packageCategories.length == 0) {
            // } else {
            //   self.categoryList = self.obj.packageCategories;
            // }
          })
          .catch(({ data }) => {
            self.toast("Error", helper.getErrorMessage(data), "danger");
          }).finally(() => {
            this.isDataLoading = false;        
          });
      } else {
        self.obj = self.getEmptyObj();

        this.api
          .getNextStockNumber()
          .then((response) => {
            self.obj.stockNo = String(response.result);
          })
          .catch(({ data }) => {}).finally(() => {
            this.isDataLoading = false;        
          });
      }
    },
    checkIfValid(fieldName) {
      // Handle nested field paths (e.g., "sale.louPurchasePrice")
      const fieldPath = fieldName.split('.');
      let field = this.$v.obj;

      for (let i = 0; i < fieldPath.length; i++) {
        if (!field || !field[fieldPath[i]]) {
          return null;
        }
        field = field[fieldPath[i]];
      }

      if (!field || !field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      // this.obj = this.getEmptyObj();
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        stockNo: "",
        vehicle: {
          chasisNo: "",
          codeModel: "",
        },
        // showRoom: {
        //   id: null,
        //   lotNo: "",
        // },
        purchase: {
          supplier: {
            name: "",
          },
        },
        advertisement: {},
        import: {
          shipName: "",
        },
        clearance: {
          approvedPermitNo: "",
          lotNo: "",
        },
        sale: {
          customer: {
            name: "",
          },

          customerId: null,
          salesmanId: null,
          depositAmount: 0,
          loan: {
            depositAmount: 0,
            bank: {
              name: "",
            },
          },
        },
        pricing: {
          recommendedSalePrice: 0,
          minimumSalePrice: 0,
        },
        registration: {
          racNo: "",
        },
        expense: {
          serviceEzCareCostAmount: 0,
          interiorCostAmount: 0,
          paintCostAmount: 0,
          tyreCostAmount: 0,
          rentalCostAmount: 0,
        },
      };
    },
    submit() {
      // Touch all fields to trigger validation
      this.$v.$touch();

      if (this.isValid) {
        this.onSubmit();
        this.submitted = true;
      } else {
        // Debug: Log all validation errors to console
        console.log('=== VALIDATION ERRORS ===');
        this.logValidationErrors(this.$v.obj);

        // Show user-friendly error message
        this.toast(
          "Validation Error",
          "Please fill in all required fields correctly. Check for fields highlighted in red. See console for details.",
          "danger"
        );

        // Scroll to first invalid field
        this.$nextTick(() => {
          const firstInvalid = document.querySelector('.is-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.focus();
          }
        });
      }
    },
    logValidationErrors(obj, path = '') {
      for (const key in obj) {
        if (key.startsWith('$')) continue; // Skip Vuelidate internal properties

        const currentPath = path ? `${path}.${key}` : key;
        const field = obj[key];

        if (field && typeof field === 'object') {
          // Check if this is a validation object
          if (field.$invalid) {
            const errors = [];
            if (field.required === false) errors.push('required');
            if (field.minLength === false) errors.push('minLength');
            if (field.numeric === false) errors.push('numeric');
            if (field.minValue === false) errors.push('minValue');
            if (field.maxValue === false) errors.push('maxValue');

            if (errors.length > 0) {
              console.log(`❌ ${currentPath}: ${errors.join(', ')}`);
            }
          }

          // Recursively check nested objects
          this.logValidationErrors(field, currentPath);
        }
      }
    },
    cancel() {
      this.$router.push({ path: "/admins/StockList" });
    },

    // Update saleAmount based on computed selling price
    updateSaleAmount() {
      const louPurchasePrice = parseFloat(this.obj.sale?.louPurchasePrice) || 0;
      const roadTax = parseFloat(this.obj.adminitrativeCost?.roadtaxAmount) || 0;
      const insurance = parseFloat(this.obj.adminitrativeCost?.insuranceAmount) || 0;
      const registrationFee = parseFloat(this.obj.adminitrativeCost?.registrationFeeAmount) || 0;

      this.obj.sale.saleAmount = louPurchasePrice - roadTax - insurance - registrationFee;
    },
    updateDepositAmount() {
      const louPurchasePrice = parseFloat(this.obj.sale?.louPurchasePrice) || 0;
      const approvedLoanAmount = parseFloat(this.obj.sale?.loan?.approvedLoanAmount) || 0;

      this.obj.sale.depositAmount = louPurchasePrice - approvedLoanAmount;
    },

    // Write the active tab key into the URL query param.
    // router.replace (not push) so tab switches don't spam browser history.
    syncTabToUrl(tabKey) {
      if (this.$route.query.tab !== tabKey) {
        this.$router.replace({ query: { ...this.$route.query, tab: tabKey } }).catch(() => {});
      }
    },
    // Read ?tab= from the URL and set the numeric activeTab index.
    // Falls back to 0 if missing, invalid, or the tab isn't visible for this user.
    syncTabFromUrl() {
      const urlTabKey = this.$route.query.tab;
      if (urlTabKey) {
        const index = this.visibleTabs.indexOf(urlTabKey);
        this.activeTab = index >= 0 ? index : 0;
      } else {
        this.activeTab = 0;
      }
    },
  },

  // Watchers for customer search functionality
  watch: {
    // User clicked a tab -> CTabs updated activeTab via .sync -> reflect it in the URL.
    activeTab(newIndex) {
      const tabKey = this.visibleTabs[newIndex];
      if (tabKey) {
        this.syncTabToUrl(tabKey);
      }
    },

    customerColumnFilterValue: {
      handler() {
        this.customerActivePage = 1; // Reset to first page when filter changes
        clearTimeout(this.customerSearchTimeout);
        this.customerSearchTimeout = setTimeout(() => {
          this.refreshTableCustomer();
        }, 300);
      },
      deep: true,
    },

    customerSorterValue: {
      handler() {
        this.refreshTableCustomer();
      },
      deep: true,
    },

    // Watch for changes in fields that affect Selling Price and Deposit Amount calculation
    'obj.sale.louPurchasePrice': {
      handler() {
        this.updateSaleAmount();
        this.updateDepositAmount();
      },
    },
    'obj.adminitrativeCost.roadtaxAmount': {
      handler() {
        this.updateSaleAmount();
      },
    },
    'obj.adminitrativeCost.insuranceAmount': {
      handler() {
        this.updateSaleAmount();
      },
    },
    'obj.adminitrativeCost.registrationFeeAmount': {
      handler() {
        this.updateSaleAmount();
      },
    },
    'obj.sale.loan.approvedLoanAmount': {
      handler() {
        this.updateDepositAmount();
      },
    },
  },
};
</script>

<style scoped>
/* Enhanced Customer Search Modal Styles */
.customer-table {
  font-size: 0.875rem;
}

.customer-info {
  line-height: 1.3;
}

.contact-info {
  font-size: 0.8125rem;
  line-height: 1.4;
}

.select-btn {
  font-weight: 600;
  min-width: 80px;
}

.modal-title {
  font-weight: 600;
  color: #321fdb;
}

.table-container {
  border: 1px solid #d8dbe0;
  border-radius: 0.375rem;
}

/* Customer Creation Modal - Ensure it appears above search modal */
.customer-create-modal {
  z-index: 1060 !important;
}

.customer-create-modal .modal-backdrop {
  z-index: 1055 !important;
}

.customer-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #d8dbe0;
}

.customer-table tbody tr:hover {
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}

.btn-close {
  border: none !important;
  box-shadow: none !important;
}

/* Loading state */
.customer-table.loading {
  opacity: 0.7;
}

/* Empty state styling */
.customer-table .text-center {
  padding: 2rem 1rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-container {
    max-height: 300px;
  }
  
  .customer-info, .contact-info {
    font-size: 0.75rem;
  }
}
</style>

<style scoped>
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px; /* Space between images */
}

.image-container {
  position: relative; /* Position relative to place buttons over the image */
  width: 100%;
  max-width: 200px; /* Set maximum width for images */
}

.image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px; /* Optional: Add some border radius for aesthetics */
}

.button-overlay {
  position: absolute; /* Position absolute to overlay on the image */
  top: 50%;
  left: 50%;
  transform: translate(
    -50%,
    -50%
  ); /* Center buttons in the middle of the image */
  display: flex;
  flex-wrap: wrap; /* Allow buttons to wrap into two columns */
  justify-content: center; /* Center the buttons horizontally */
  align-items: center; /* Center the buttons vertically */
  gap: 5px; /* Space between buttons */
  opacity: 0; /* Hide buttons by default */
  transition: opacity 0.3s; /* Smooth transition for hover effect */
}

.image-container:hover .button-overlay {
  opacity: 1; /* Show buttons on hover */
}

.button-row {
  display: flex; /* Display buttons in a row */
  gap: 5px; /* Space between buttons */
}

.move-button {
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%; /* Circular buttons */
}

.maximized-image {
  width: 100%;
  height: auto;
  max-width: 100%;
  border-radius: 8px; /* Optional: Add some border radius for aesthetics */
}
.default-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: yellow;
  color: black;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: bold;
}
</style>


<!-- 
<style scoped>
.image-container {
  position: relative;
  width: 100%;
}

.image-overlay {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.action-button {
  margin: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.default-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: yellow;
  color: black;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: bold;
}
</style> -->
<!-- 
<style scoped>
.image-container {
  position: relative;
  width: 100%;
}

.image-overlay {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.action-button {
  margin: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.default-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: yellow;
  color: black;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: bold;
}
</style> -->
<style scoped>
/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .stock-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }
}
</style>
